import { useState, useCallback, useRef, useMemo } from 'react';
import {
  Upload,
  FileText,
  CheckCircle2,
  AlertCircle,
  X,
  Plus,
  Trash2,
  Loader2,
  Eye,
  Pencil,
  Link2,
  Search,
  DollarSign,
  RotateCcw,
  Download,
  Info,
  SkipForward,
  Package,
  SearchX,
  Receipt,
  ClipboardCheck,
} from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import {
  useVendorInvoices,
  useVendorInvoice,
  useUploadInvoice,
  useParseInvoice,
  useUpdateInvoice,
  useUpdateInvoiceLine,
  useAddInvoiceLine,
  useDeleteInvoiceLine,
  useRestoreInvoiceLine,
  useApproveInvoice,
  useRejectInvoice,
  useMatchProducts,
  useApplyLinePrice,
  useConfirmUnit,
  useRevertInventory,
  useProductPriceHistory,
  useUpdateInventory,
  useApprovalPreview,
  useInvoiceAttachments,
  useUploadAttachment,
  useDeleteAttachment,
  type VendorInvoice,
  type VendorInvoiceLine,
  type VendorInvoiceDetail,
  useInvoicePdfUrl,
  useVendorInvoiceBills,
  usePayBill,
  useSuggestPO,
  useLinkPO,
} from '@/hooks/use-vendor-invoices';
import { useSPPayees, useLockedFundingAccount, type SPPayee } from '@/hooks/use-send-payments';
import { useProducts, useCreateProduct } from '@/hooks/use-products';
import { useBarcodeScan } from '@/hooks/use-barcode-scan';
import { StatusBadge } from '@/components/shared/status-badge';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { DateInput } from '@/components/ui/date-input';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { toast } from '@/stores/toast-store';
import { useLocationStore } from '@/stores/location-store';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/use-permissions';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

// ── Export Helpers ──

interface ExportInvoice {
  invoiceNumber: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  vendorName: string;
  subtotal: string;
  taxAmount: string;
  totalAmount: string;
  creditAmount: string;
  status: string;
  lines: VendorInvoiceLine[];
}

function downloadInvoiceXlsx(invoice: ExportInvoice) {
  const activeLines = invoice.lines.filter((l) => !l.isExcluded);
  const headers = [
    '#',
    'Description',
    'SKU',
    'Product',
    'Barcode',
    'Qty',
    'Unit',
    'Unit Price',
    'Amount',
    'Price Applied',
  ];
  const rows = activeLines.map((l) => [
    l.lineNumber,
    l.description,
    l.vendorItemCode || '',
    l.productName || '',
    l.productBarcode || '',
    l.quantity,
    l.unitOfMeasure || 'each',
    l.unitPrice,
    l.amount,
    l.priceApplied ? 'Yes' : 'No',
  ]);

  // Build CSV (Excel-compatible)
  const csvRows = [headers, ...rows].map((row) =>
    row
      .map((cell) => {
        const str = String(cell ?? '');
        return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
      })
      .join(','),
  );

  // Add summary rows
  csvRows.push('');
  csvRows.push(`Subtotal,,,,,,,,${invoice.subtotal},`);
  if (Number(invoice.creditAmount) > 0) csvRows.push(`Credits,,,,,,,,${invoice.creditAmount},`);
  if (Number(invoice.taxAmount) > 0) csvRows.push(`Tax,,,,,,,,${invoice.taxAmount},`);
  csvRows.push(`Total,,,,,,,,${invoice.totalAmount},`);

  const blob = new Blob(['﻿' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Invoice-${invoice.invoiceNumber || 'draft'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadInvoicePdf(invoice: ExportInvoice) {
  const activeLines = invoice.lines.filter((l) => !l.isExcluded);
  // Generate a printable HTML invoice and trigger print/save as PDF
  const html = `
<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>Invoice ${invoice.invoiceNumber || ''}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 40px; color: #333; font-size: 13px; }
  h1 { font-size: 24px; margin-bottom: 5px; }
  .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
  .header-right { text-align: right; }
  .meta { margin-bottom: 20px; }
  .meta span { display: inline-block; margin-right: 30px; }
  .meta label { color: #888; font-size: 11px; display: block; }
  table { width: 100%; border-collapse: collapse; margin-top: 15px; }
  th { background: #2c3e50; color: white; text-align: left; padding: 8px 10px; font-size: 11px; }
  th:nth-child(n+6) { text-align: right; }
  td { padding: 7px 10px; border-bottom: 1px solid #eee; }
  td:nth-child(n+6) { text-align: right; }
  .totals { margin-top: 20px; text-align: right; }
  .totals div { margin-bottom: 4px; }
  .totals .total { font-weight: bold; font-size: 16px; border-top: 2px solid #333; padding-top: 6px; }
  .status { display: inline-block; padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; }
  .status-approved { background: #d1fae5; color: #065f46; }
  .status-paid { background: #dbeafe; color: #1e40af; }
  .credit { color: #16a34a; }
  .excluded { text-decoration: line-through; color: #999; }
  .price-applied { color: #16a34a; font-size: 10px; }
  @media print { body { margin: 20px; } }
</style>
</head><body>
<div class="header">
  <div>
    <h1>${invoice.vendorName}</h1>
    <span class="status status-${invoice.status}">${invoice.status.toUpperCase()}</span>
  </div>
  <div class="header-right">
    <h1>INVOICE</h1>
    <div>${invoice.invoiceNumber || ''}</div>
  </div>
</div>
<div class="meta">
  <span><label>Invoice Date</label>${invoice.invoiceDate || '—'}</span>
  <span><label>Due Date</label>${invoice.dueDate || '—'}</span>
  <span><label>Vendor</label>${invoice.vendorName}</span>
</div>
<table>
  <thead><tr>
    <th>#</th><th>Description</th><th>SKU</th><th>Product</th><th>Barcode</th>
    <th>Qty</th><th>Unit</th><th>Price</th><th>Amount</th>
  </tr></thead>
  <tbody>
    ${activeLines
      .map(
        (l) => `<tr class="${l.isCredit ? 'credit' : ''}">
      <td>${l.lineNumber}</td>
      <td>${l.description}${l.priceApplied ? ' <span class="price-applied">✓ price applied</span>' : ''}</td>
      <td>${l.vendorItemCode || ''}</td>
      <td>${l.productName || '—'}</td>
      <td>${l.productBarcode || ''}</td>
      <td>${l.quantity}</td>
      <td>${l.unitOfMeasure || 'each'}</td>
      <td>$${Number(l.unitPrice).toFixed(2)}</td>
      <td>${l.isCredit ? '-' : ''}$${Math.abs(Number(l.amount)).toFixed(2)}</td>
    </tr>`,
      )
      .join('')}
  </tbody>
</table>
<div class="totals">
  <div>Subtotal: $${Number(invoice.subtotal).toFixed(2)}</div>
  ${Number(invoice.creditAmount) > 0 ? `<div class="credit">Credits: -$${Number(invoice.creditAmount).toFixed(2)}</div>` : ''}
  ${Number(invoice.taxAmount) > 0 ? `<div>Tax: $${Number(invoice.taxAmount).toFixed(2)}</div>` : ''}
  <div class="total">Total: $${Number(invoice.totalAmount).toFixed(2)}</div>
</div>
</body></html>`;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  }
}

// ── Create Invoice Tab ──

const INVOICE_STATUSES = ['uploaded', 'parsed', 'review', 'approved', 'paid', 'rejected'];

export function CreateInvoiceTab() {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const { selectedLocationId } = useLocationStore();
  const { data: invoicesData, isLoading } = useVendorInvoices();
  const canUpload = usePermissions().has('sp.invoices.upload');

  // Close open invoice when location changes
  const [prevLocationId, setPrevLocationId] = useState(selectedLocationId);
  if (selectedLocationId !== prevLocationId) {
    setPrevLocationId(selectedLocationId);
    setSelectedInvoiceId(null);
    setShowUpload(false);
  }
  const invoices = invoicesData?.data ?? [];

  const filteredInvoices = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices.filter((inv) => {
      if (statusFilter && inv.status !== statusFilter) return false;
      if (!q) return true;
      const vendor = (inv.payee?.name || inv.vendorNameRaw || '').toLowerCase();
      return vendor.includes(q);
    });
  }, [invoices, search, statusFilter]);

  const total = filteredInvoices.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(
    () => filteredInvoices.slice((page - 1) * perPage, page * perPage),
    [filteredInvoices, page, perPage],
  );

  if (selectedInvoiceId) {
    return <InvoiceReviewPanel invoiceId={selectedInvoiceId} onBack={() => setSelectedInvoiceId(null)} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-foreground">Vendor Invoices</h3>
          <div className="relative flex-1 lg:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by vendor name…"
              className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-lg border border-input bg-background px-3 text-sm capitalize"
          >
            <option value="">All Statuses</option>
            {INVOICE_STATUSES.map((s) => (
              <option key={s} value={s} className="capitalize">
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
          {canUpload && (
            <button
              onClick={() => setShowUpload(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
            >
              <Upload className="h-4 w-4" /> Upload Invoice
            </button>
          )}
        </div>
      </div>

      {showUpload && canUpload && (
        <InvoiceUploadZone
          onClose={() => setShowUpload(false)}
          onUploaded={(id) => {
            setShowUpload(false);
            setSelectedInvoiceId(id);
          }}
        />
      )}

      {isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border">
          <EmptyState
            icon={FileText}
            title="No invoices yet"
            description="Upload a vendor invoice PDF to get started."
          />
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border">
          <EmptyState
            icon={SearchX}
            title="No matching invoices"
            description="Try a different vendor name or status filter."
          />
        </div>
      ) : (
        <>
          <InvoiceList invoices={pageRows} onSelect={setSelectedInvoiceId} />
          <DataTablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            perPage={perPage}
            onPageChange={setPage}
            perPageOptions={PER_PAGE_OPTIONS}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        </>
      )}
    </div>
  );
}

// ── Invoice List ──

function InvoiceList({ invoices, onSelect }: { invoices: VendorInvoice[]; onSelect: (id: string) => void }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-muted/50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Invoice #</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Vendor</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Date</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">Amount</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Status</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border bg-white">
          {invoices.map((inv) => (
            <tr key={inv.id} className="hover:bg-muted/30 cursor-pointer" onClick={() => onSelect(inv.id)}>
              <td className="px-4 py-3 text-sm font-medium text-foreground">
                {inv.invoiceNumber || inv.pdfFilename || inv.id.slice(0, 8)}
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">{inv.payee?.name || inv.vendorNameRaw || '—'}</td>
              <td className="px-4 py-3 text-sm text-muted-foreground">
                {inv.invoiceDate || formatDateTime(inv.createdAt)}
              </td>
              <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(inv.totalAmount)}</td>
              <td className="px-4 py-3">
                <StatusBadge status={inv.status} />
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(inv.id);
                  }}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <Eye className="h-3.5 w-3.5" /> View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Upload Zone ──

function InvoiceUploadZone({ onClose, onUploaded }: { onClose: () => void; onUploaded: (id: string) => void }) {
  const uploadInvoice = useUploadInvoice();
  const parseInvoice = useParseInvoice();
  const { selectedLocationId } = useLocationStore();
  const updateInvoice = useUpdateInvoice();
  const [isDragging, setIsDragging] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    async (file: File) => {
      if (file.type !== 'application/pdf') {
        toast.error('Only PDF files are accepted');
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        toast.error('File must be under 15MB');
        return;
      }

      try {
        setProcessing('Uploading...');
        const result = await uploadInvoice.mutateAsync(file);
        const invoiceId = result.data.id;

        if (selectedLocationId) {
          await updateInvoice.mutateAsync({ id: invoiceId, locationId: selectedLocationId });
        }

        setProcessing('Parsing invoice with AI...');
        await parseInvoice.mutateAsync(invoiceId);

        toast.success('Invoice parsed successfully');
        onUploaded(invoiceId);
      } catch (err: unknown) {
        const msg = (err as { error?: { message?: string } })?.error?.message || 'Failed to process invoice';
        toast.error(msg);
        setProcessing(null);
      }
    },
    [uploadInvoice, parseInvoice, updateInvoice, selectedLocationId, onUploaded],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  if (processing) {
    return (
      <div className="flex h-64 flex-col items-center justify-center rounded-lg border-2 border-dashed border-primary/30 bg-primary/5">
        <Loader2 className="mb-3 h-8 w-8 animate-spin text-primary" />
        <p className="text-sm font-medium text-primary">{processing}</p>
        <p className="mt-1 text-xs text-muted-foreground">This may take a few seconds...</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <button onClick={onClose} className="absolute right-2 top-2 rounded-md p-1 hover:bg-muted z-10">
        <X className="h-4 w-4 text-muted-foreground" />
      </button>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`flex h-64 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
          isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'
        }`}
      >
        <Upload className="mb-3 h-8 w-8 text-muted-foreground" />
        <p className="text-sm">
          <span className="font-medium text-primary">Click</span>
          <span className="text-muted-foreground"> or </span>
          <span className="font-medium text-primary">Drop</span>
          <span className="text-muted-foreground"> files to upload</span>
        </p>
        <p className="mt-1 text-xs text-muted-foreground">PDF only. Max size 15MB.</p>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />
    </div>
  );
}

// ── Invoice Review Panel ──

function InvoiceReviewPanel({ invoiceId, onBack }: { invoiceId: string; onBack: () => void }) {
  const { data: invoiceData, isLoading } = useVendorInvoice(invoiceId);
  const { data: pdfData } = useInvoicePdfUrl(invoiceId);
  const approveInvoice = useApproveInvoice();
  const rejectInvoice = useRejectInvoice();
  const updateInvoice = useUpdateInvoice();
  const matchProductsMut = useMatchProducts();
  const revertInventory = useRevertInventory();
  const { user } = useAuthStore();
  const perms = usePermissions();
  const canEditInvoice = perms.has('sp.invoices.edit');
  const canApproveInvoice = perms.has('sp.invoices.approve');
  const canChangeInventory = perms.has('sp.invoices.inventory');
  // Auto-pay schedules a payment, so it has its own key.
  const canAutoPay = perms.has('sp.invoices.autopay');
  // Profitability analysis stays owner-only by role.
  const isOwner = user?.role === 'owner' || user?.role === 'super_admin';
  const updateInventoryMut = useUpdateInventory();
  const [updateInventory, setUpdateInventory] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showInventoryConfirm, setShowInventoryConfirm] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [notes, setNotes] = useState('');
  const [notesLoaded, setNotesLoaded] = useState(false);
  const [lineFilter, setLineFilter] = useState('all');
  const [linePage, setLinePage] = useState(0);
  const LINE_PAGE_SIZE = 10;
  const [pdfWidth, setPdfWidth] = useState(50);
  const isDraggingRef = useRef(false);

  const invoice = invoiceData?.data;
  const pdfUrl = pdfData?.data?.url;

  // Initialize notes from invoice data once
  if (invoice && !notesLoaded) {
    setNotes(invoice.notes || '');
    setNotesLoaded(true);
  }

  if (isLoading || !invoice) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Open for changes by status; header, lines, PO link and attachments also
  // need the edit key, approve and reject their own.
  const statusOpen = !['approved', 'paid', 'rejected'].includes(invoice.status);
  const isEditable = statusOpen && canEditInvoice;
  const activeLines = invoice.lines.filter((l) => !l.isExcluded);
  const partialMatchCount = activeLines.filter((l) => !l.isMatched && !!l.productId && !l.isCredit).length;
  const noMatchCount = activeLines.filter((l) => !l.isMatched && !l.productId && !l.isCredit).length;
  const unmappedCount = partialMatchCount + noMatchCount;
  const allLinesMapped = unmappedCount === 0;
  const matchedNonCredit = activeLines.filter((l) => l.isMatched && !l.isCredit);
  const unappliedPriceCount = matchedNonCredit.filter((l) => !l.priceApplied).length;
  const allPricesApplied = unappliedPriceCount === 0;
  const canApprove =
    ['processing', 'review'].includes(invoice.status) && !!invoice.payeeId && allLinesMapped && allPricesApplied;

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    try {
      await rejectInvoice.mutateAsync({ id: invoiceId, reason: rejectReason });
      toast.success('Invoice rejected');
      onBack();
    } catch (err: unknown) {
      toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to reject');
    }
  };

  const handleVendorChange = async (payeeId: string) => {
    try {
      await updateInvoice.mutateAsync({ id: invoiceId, payeeId });
      await matchProductsMut.mutateAsync(invoiceId);
      toast.success('Vendor linked and products re-matched');
    } catch (err: unknown) {
      toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to link vendor');
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-white overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-border px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="rounded-md p-1.5 hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Invoice {invoice.invoiceNumber || invoice.pdfFilename || ''}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              <StatusBadge status={invoice.status} />
              {(invoice as any).paymentSource === 'auto_pay' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                  Auto-pay
                </span>
              )}
              {invoice.vendorNameRaw && !invoice.payeeId && (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> Vendor not linked
                </span>
              )}
            </div>
          </div>
        </div>

        {statusOpen && canApproveInvoice && (
          <div className="flex items-center gap-2">
            {['processing', 'review'].includes(invoice.status) && (!allLinesMapped || !allPricesApplied) && (
              <div className="flex flex-col items-end gap-0.5 mr-1">
                {!allLinesMapped && (
                  <span className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {unmappedCount} item{unmappedCount !== 1 ? 's' : ''} need matching
                  </span>
                )}
                {allLinesMapped && !allPricesApplied && (
                  <span className="text-xs text-blue-600 flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    {unappliedPriceCount} price{unappliedPriceCount !== 1 ? 's' : ''} need review
                  </span>
                )}
              </div>
            )}
            <button
              onClick={() => setShowRejectModal(true)}
              className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
            >
              Reject
            </button>
            <button
              onClick={() => setShowApproveConfirm(true)}
              disabled={!canApprove || approveInvoice.isPending}
              className="rounded-lg bg-green-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
              title={
                !allLinesMapped
                  ? 'Match all line items to products before approving'
                  : !allPricesApplied
                    ? 'Apply or review all prices before approving'
                    : ''
              }
            >
              {approveInvoice.isPending ? 'Approving...' : 'Approve'}
            </button>
          </div>
        )}

        {/* Download options for approved/paid invoices */}
        {['approved', 'paid'].includes(invoice.status) && (
          <div className="flex items-center gap-2">
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/50"
              >
                <Download className="h-3.5 w-3.5" /> Original PDF
              </a>
            )}
            <button
              onClick={() =>
                downloadInvoicePdf({
                  invoiceNumber: invoice.invoiceNumber,
                  invoiceDate: invoice.invoiceDate,
                  dueDate: invoice.dueDate,
                  vendorName: invoice.payee?.name || invoice.vendorNameRaw || 'Unknown',
                  subtotal: invoice.subtotal,
                  taxAmount: invoice.taxAmount,
                  totalAmount: invoice.totalAmount,
                  creditAmount: invoice.creditAmount,
                  status: invoice.status,
                  lines: invoice.lines,
                })
              }
              className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/50"
            >
              <Download className="h-3.5 w-3.5" /> Invoice PDF
            </button>
            <button
              onClick={() =>
                downloadInvoiceXlsx({
                  invoiceNumber: invoice.invoiceNumber,
                  invoiceDate: invoice.invoiceDate,
                  dueDate: invoice.dueDate,
                  vendorName: invoice.payee?.name || invoice.vendorNameRaw || 'Unknown',
                  subtotal: invoice.subtotal,
                  taxAmount: invoice.taxAmount,
                  totalAmount: invoice.totalAmount,
                  creditAmount: invoice.creditAmount,
                  status: invoice.status,
                  lines: invoice.lines,
                })
              }
              className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/50"
            >
              <Download className="h-3.5 w-3.5" /> Excel
            </button>
          </div>
        )}
      </div>

      {/* Side-by-side layout with resizable PDF panel */}
      <div className="flex h-[calc(100vh-56px)]">
        {/* Left: PDF Preview */}
        <div className="shrink-0 overflow-hidden bg-gray-100 border-r border-border" style={{ width: `${pdfWidth}%` }}>
          {pdfUrl ? (
            <iframe src={pdfUrl} className="w-full h-full" title="Invoice PDF" />
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground text-sm">
              PDF preview not available
            </div>
          )}
        </div>

        {/* Drag handle */}
        <div
          className="w-2 shrink-0 cursor-col-resize bg-border hover:bg-primary/40 active:bg-primary/50 transition-colors relative group"
          onMouseDown={(e) => {
            e.preventDefault();
            isDraggingRef.current = true;
            const startX = e.clientX;
            const startWidth = pdfWidth;
            // Add overlay to prevent iframe from stealing mouse events
            const overlay = document.createElement('div');
            overlay.style.cssText = 'position:fixed;inset:0;z-index:50;cursor:col-resize;';
            document.body.appendChild(overlay);
            const onMouseMove = (ev: MouseEvent) => {
              if (!isDraggingRef.current) return;
              const delta = ev.clientX - startX;
              const newWidth = startWidth + (delta / window.innerWidth) * 100;
              setPdfWidth(Math.max(20, Math.min(70, newWidth)));
            };
            const onMouseUp = () => {
              isDraggingRef.current = false;
              overlay.remove();
              document.removeEventListener('mousemove', onMouseMove);
              document.removeEventListener('mouseup', onMouseUp);
            };
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
          }}
          title="Drag to resize"
        >
          {/* Grip dots */}
          <div className="absolute inset-y-0 flex items-center justify-center">
            <div className="flex flex-col gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
              <div className="w-1 h-1 rounded-full bg-muted-foreground" />
              <div className="w-1 h-1 rounded-full bg-muted-foreground" />
              <div className="w-1 h-1 rounded-full bg-muted-foreground" />
            </div>
          </div>
        </div>

        {/* Right: Invoice Details */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Invoice Header Info */}
          <InvoiceHeaderForm invoice={invoice} isEditable={isEditable} onVendorChange={handleVendorChange} />

          {/* PO Linking */}
          {isEditable && invoice.payeeId && !invoice.purchaseOrderId && <POLinkSection invoiceId={invoiceId} />}
          {invoice.purchaseOrderId && (
            <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50/50 p-3 text-sm">
              <Link2 className="h-4 w-4 text-green-600" />
              <span className="font-medium text-green-700">Linked to PO</span>
              <span className="text-xs text-muted-foreground">ID: {invoice.purchaseOrderId.slice(0, 8)}</span>
            </div>
          )}

          {/* Line Items with filters and pagination */}
          {(() => {
            const filteredLines = invoice.lines.filter((line) => {
              if (lineFilter === 'all') return true;
              if (lineFilter === 'matched') return line.isMatched;
              if (lineFilter === 'unmatched') return !line.isMatched && !line.isCredit && !line.isExcluded;
              if (lineFilter === 'auto-approved')
                return line.priceApplied && line.priceChangePct != null && Math.abs(line.priceChangePct) < 1;
              if (lineFilter === 'price-applied')
                return (
                  line.priceApplied &&
                  !line.priceSkipped &&
                  (line.priceChangePct == null || Math.abs(line.priceChangePct) >= 1)
                );
              if (lineFilter === 'price-skipped') return line.priceSkipped;
              if (lineFilter === 'price-pending') return line.isMatched && !line.priceApplied && !line.isCredit;
              if (lineFilter === 'excluded') return line.isExcluded;
              if (lineFilter === 'credit') return line.isCredit;
              return true;
            });
            const totalFiltered = filteredLines.length;
            const totalPages = Math.ceil(totalFiltered / LINE_PAGE_SIZE);
            const safePage = Math.min(linePage, Math.max(0, totalPages - 1));
            const pageLines = filteredLines.slice(safePage * LINE_PAGE_SIZE, (safePage + 1) * LINE_PAGE_SIZE);

            return (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-foreground">
                    Line Items ({invoice.lines.length})
                    {partialMatchCount > 0 && (
                      <span className="ml-2 text-xs text-amber-600">{partialMatchCount} needs confirmation</span>
                    )}
                    {noMatchCount > 0 && <span className="ml-2 text-xs text-red-600">{noMatchCount} no match</span>}
                  </h4>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-1">
                  {[
                    { key: 'all', label: 'All', count: invoice.lines.length },
                    { key: 'matched', label: 'Matched', count: invoice.lines.filter((l) => l.isMatched).length },
                    {
                      key: 'unmatched',
                      label: 'Unmatched',
                      count: invoice.lines.filter((l) => !l.isMatched && !l.isCredit && !l.isExcluded).length,
                    },
                    {
                      key: 'auto-approved',
                      label: 'Auto-approved',
                      count: invoice.lines.filter(
                        (l) => l.priceApplied && l.priceChangePct != null && Math.abs(l.priceChangePct) < 1,
                      ).length,
                    },
                    {
                      key: 'price-applied',
                      label: 'Price applied',
                      count: invoice.lines.filter(
                        (l) =>
                          l.priceApplied &&
                          !l.priceSkipped &&
                          (l.priceChangePct == null || Math.abs(l.priceChangePct) >= 1),
                      ).length,
                    },
                    {
                      key: 'price-skipped',
                      label: 'Skipped',
                      count: invoice.lines.filter((l) => l.priceSkipped).length,
                    },
                    {
                      key: 'price-pending',
                      label: 'Price pending',
                      count: invoice.lines.filter((l) => l.isMatched && !l.priceApplied && !l.isCredit).length,
                    },
                    {
                      key: 'excluded',
                      label: 'Excluded',
                      count: invoice.lines.filter((l) => l.isExcluded).length,
                    },
                  ]
                    .filter((f) => f.key === 'all' || f.count > 0)
                    .map((f) => (
                      <button
                        key={f.key}
                        onClick={() => {
                          setLineFilter(f.key);
                          setLinePage(0);
                        }}
                        className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
                          lineFilter === f.key
                            ? 'bg-primary text-white'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        {f.label} ({f.count})
                      </button>
                    ))}
                </div>

                <div className="space-y-2">
                  {pageLines.map((line) => (
                    <LineItemCard
                      key={line.id}
                      line={line}
                      invoiceId={invoiceId}
                      isEditable={isEditable}
                      payeeId={invoice.payeeId}
                    />
                  ))}
                  {totalFiltered === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">No items match this filter</p>
                  )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[10px] text-muted-foreground">
                      {safePage * LINE_PAGE_SIZE + 1}–{Math.min((safePage + 1) * LINE_PAGE_SIZE, totalFiltered)} of{' '}
                      {totalFiltered}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setLinePage(Math.max(0, safePage - 1))}
                        disabled={safePage === 0}
                        className="rounded border border-border px-2 py-0.5 text-[10px] disabled:opacity-30"
                      >
                        Prev
                      </button>
                      <span className="text-[10px] text-muted-foreground">
                        {safePage + 1}/{totalPages}
                      </span>
                      <button
                        onClick={() => setLinePage(Math.min(totalPages - 1, safePage + 1))}
                        disabled={safePage >= totalPages - 1}
                        className="rounded border border-border px-2 py-0.5 text-[10px] disabled:opacity-30"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}

                {isEditable && <AddLineButton invoiceId={invoiceId} />}
              </div>
            );
          })()}

          {/* Inventory Status & Controls */}
          {invoice.updateInventory && invoice.inventoryUpdatedAt ? (
            <div className="rounded-lg border border-green-200 bg-green-50/50 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Inventory Updated</p>
                    <p className="text-xs text-green-600">
                      Quantities added on {formatDateTime(invoice.inventoryUpdatedAt)}
                    </p>
                  </div>
                </div>
                {canChangeInventory && (
                  <button
                    onClick={async () => {
                      try {
                        await revertInventory.mutateAsync(invoiceId);
                        toast.success('Inventory quantities reverted');
                      } catch (err: unknown) {
                        toast.error(
                          (err as { error?: { message?: string } })?.error?.message || 'Failed to revert inventory',
                        );
                      }
                    }}
                    disabled={revertInventory.isPending}
                    className="inline-flex items-center gap-1 rounded border border-red-200 bg-white px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    <RotateCcw className="h-3 w-3" />
                    {revertInventory.isPending ? 'Reverting...' : 'Revert Inventory'}
                  </button>
                )}
              </div>
            </div>
          ) : canApprove && (canApproveInvoice || canAutoPay) ? (
            <div className="space-y-2">
              {canApproveInvoice && canChangeInventory && (
                <label className="flex items-center gap-2 rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/30">
                  <input
                    type="checkbox"
                    checked={updateInventory}
                    onChange={(e) => setUpdateInventory(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground">Update inventory quantities</p>
                    <p className="text-xs text-muted-foreground">
                      Add matched item quantities to your product inventory on approval
                    </p>
                  </div>
                </label>
              )}
              <label className="flex items-center gap-2 rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/30">
                <input
                  type="checkbox"
                  checked={invoice.autoPay ?? false}
                  disabled={!canAutoPay}
                  title={canAutoPay ? undefined : 'Needs the Turn on auto-pay permission'}
                  onChange={async (e) => {
                    try {
                      await updateInvoice.mutateAsync({ id: invoiceId, autoPay: e.target.checked });
                    } catch (err: unknown) {
                      /* ignore */
                    }
                  }}
                  className="rounded border-gray-300"
                />
                <div>
                  <p className="text-sm font-medium text-foreground">Auto-pay on due date</p>
                  <p className="text-xs text-muted-foreground">
                    Automatically create payment when due date ({invoice.dueDate || 'not set'}) is reached
                  </p>
                </div>
              </label>
            </div>
          ) : ['approved', 'paid'].includes(invoice.status) && !invoice.updateInventory ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50/30 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Inventory Not Updated</p>
                    <p className="text-xs text-amber-600">This invoice was approved without updating inventory</p>
                  </div>
                </div>
                {canChangeInventory && (
                  <button
                    onClick={() => setShowInventoryConfirm(true)}
                    className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90"
                  >
                    Update Inventory
                  </button>
                )}
              </div>
            </div>
          ) : null}

          {/* Auto-pay status for approved invoices */}
          {['approved'].includes(invoice.status) && invoice.autoPay && invoice.dueDate && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/30 p-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Auto-pay enabled</p>
                  <p className="text-xs text-blue-600">Payment will be created automatically on {invoice.dueDate}</p>
                </div>
              </div>
            </div>
          )}

          {/* Invoice Totals */}
          <div className="rounded-lg border border-border p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
            </div>
            {Number(invoice.taxAmount) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax</span>
                <span>{formatCurrency(invoice.taxAmount)}</span>
              </div>
            )}
            {Number(invoice.creditAmount) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Credits</span>
                <span className="text-green-600">-{formatCurrency(invoice.creditAmount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm font-semibold border-t border-border pt-2">
              <span>Total</span>
              <span>{formatCurrency(invoice.totalAmount)}</span>
            </div>
          </div>

          {/* Profitability Analysis — owner/super_admin only */}
          {isOwner &&
            (() => {
              const matchedNonCredit = activeLines.filter(
                (l) => l.isMatched && !l.isCredit && l.productSellingPrice != null,
              );
              if (matchedNonCredit.length === 0) return null;

              const totalCost = matchedNonCredit.reduce((sum, l) => sum + Number(l.amount), 0);
              const totalRevenue = matchedNonCredit.reduce((sum, l) => {
                const qty = Number(l.quantity);
                const sellingPrice = l.productSellingPrice || 0;
                return sum + qty * sellingPrice;
              }, 0);
              const grossProfit = totalRevenue - totalCost;
              const grossMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;

              return (
                <div className="rounded-lg border border-border p-4">
                  <p className="text-xs font-medium text-foreground mb-3">Profitability Analysis</p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-[10px] text-muted-foreground">Potential POS Revenue</p>
                      <p className="text-lg font-bold text-blue-600">{formatCurrency(totalRevenue)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">Average Gross Margin</p>
                      <p
                        className={`text-lg font-bold ${grossMargin >= 30 ? 'text-green-600' : grossMargin >= 15 ? 'text-amber-600' : 'text-red-600'}`}
                      >
                        {grossMargin.toFixed(1)}%
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">Total Gross Profit</p>
                      <p className={`text-lg font-bold ${grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(grossProfit)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* Price Update Tracking */}
          {activeLines.filter((l) => l.isMatched && !l.isCredit).length > 0 && (
            <div className="rounded-lg border border-border p-3">
              <p className="text-xs font-medium text-foreground mb-2">Price Updates</p>
              <div className="space-y-1">
                {(() => {
                  const matchedItems = activeLines.filter((l) => l.isMatched && !l.isCredit);
                  const appliedCount = matchedItems.filter((l) => l.priceApplied).length;
                  const pendingCount = matchedItems.length - appliedCount;
                  return (
                    <>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Matched items</span>
                        <span className="font-medium">{matchedItems.length}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle2 className="h-3 w-3" /> Price applied
                        </span>
                        <span className="font-medium text-green-600">{appliedCount}</span>
                      </div>
                      {pendingCount > 0 && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-1 text-amber-600">
                            <AlertCircle className="h-3 w-3" /> Pending
                          </span>
                          <span className="font-medium text-amber-600">{pendingCount}</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Attachments */}
          <InvoiceAttachments invoiceId={invoiceId} isEditable={isEditable} />

          {/* Notes / Comments */}
          <div className="rounded-lg border border-border p-3 space-y-2">
            <label className="text-xs font-medium text-foreground">Notes</label>
            <textarea
              value={notes}
              readOnly={!canEditInvoice}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={async () => {
                if (canEditInvoice && notes !== (invoice.notes || '')) {
                  try {
                    await updateInvoice.mutateAsync({ id: invoiceId, notes });
                  } catch (err: unknown) {
                    toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to save notes');
                  }
                }
              }}
              placeholder="Add comments or notes about this invoice..."
              className="w-full rounded border border-border p-2 text-sm resize-none h-20 focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
            {updateInvoice.isPending && <p className="text-[10px] text-muted-foreground">Saving...</p>}
          </div>
        </div>
      </div>

      {/* Inventory Update Confirmation Dialog */}
      {showInventoryConfirm && (
        <InventoryConfirmDialog
          invoiceId={invoiceId}
          onConfirm={async () => {
            try {
              await updateInventoryMut.mutateAsync(invoiceId);
              setShowInventoryConfirm(false);
              toast.success('Inventory updated successfully');
            } catch (err: unknown) {
              toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to update inventory');
            }
          }}
          onCancel={() => setShowInventoryConfirm(false)}
          isPending={updateInventoryMut.isPending}
        />
      )}

      {/* Approve Confirmation Dialog */}
      {showApproveConfirm && (
        <ApproveConfirmDialog
          invoiceId={invoiceId}
          updateInventory={updateInventory}
          onConfirm={async () => {
            try {
              await approveInvoice.mutateAsync({ id: invoiceId, updateInventory });
              setShowApproveConfirm(false);
              toast.success('Invoice approved');
              onBack();
            } catch (err: unknown) {
              toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to approve');
            }
          }}
          onCancel={() => setShowApproveConfirm(false)}
          isPending={approveInvoice.isPending}
        />
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-sm font-semibold mb-3">Reject Invoice</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."
              className="w-full rounded-lg border border-border p-3 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || rejectInvoice.isPending}
                className="rounded-lg bg-red-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {rejectInvoice.isPending ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Invoice Header Form ──

function InvoiceHeaderForm({
  invoice,
  isEditable,
  onVendorChange,
}: {
  invoice: VendorInvoiceDetail;
  isEditable: boolean;
  onVendorChange: (payeeId: string) => void;
}) {
  // Fetch payees without location scoping so vendor detection works for all payees
  const { data: payeeData } = useSPPayees({ locationId: '' });
  const payees = (payeeData?.data ?? []).filter((p: SPPayee) => p.isActive);
  const [showManualSelect, setShowManualSelect] = useState(false);
  // Local due-date so the picker value sticks after selection (the save is
  // fire-and-forget to the API, with no invoice refetch feeding a controlled value).
  const [dueDate, setDueDate] = useState(invoice.dueDate || '');

  // Find suggested vendor match from the raw name
  const suggestedPayee =
    !invoice.payeeId && invoice.vendorNameRaw
      ? (() => {
          const raw = invoice.vendorNameRaw.toLowerCase();
          return (
            payees.find(
              (p: SPPayee) =>
                p.name.toLowerCase() === raw ||
                p.name.toLowerCase().includes(raw) ||
                raw.includes(p.name.toLowerCase()),
            ) || null
          );
        })()
      : null;

  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      {/* Vendor */}
      <div>
        <label className="text-xs font-medium text-muted-foreground">Vendor</label>
        {invoice.payeeId ? (
          <div className="mt-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">{invoice.payee?.name || 'Linked'}</span>
                {invoice.payee?.company && (
                  <span className="text-xs text-muted-foreground">({invoice.payee.company})</span>
                )}
              </div>
              {isEditable && (
                <button onClick={() => setShowManualSelect(true)} className="text-[10px] text-primary hover:underline">
                  Change vendor
                </button>
              )}
            </div>
            {showManualSelect && isEditable && (
              <div className="mt-2">
                <SearchableSelect
                  options={payees.map((p: SPPayee) => ({
                    value: p.id,
                    label: p.name,
                    subtitle: p.company || undefined,
                  }))}
                  value={invoice.payeeId}
                  onChange={(val) => {
                    onVendorChange(val);
                    setShowManualSelect(false);
                  }}
                  placeholder="Select different vendor..."
                />
              </div>
            )}
          </div>
        ) : suggestedPayee && !showManualSelect ? (
          <div className="mt-1 space-y-2">
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-2">
              <p className="text-xs text-amber-700 mb-1.5">
                Detected: <span className="font-medium">"{invoice.vendorNameRaw}"</span>
              </p>
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{suggestedPayee.name}</p>
                  {suggestedPayee.company && <p className="text-xs text-muted-foreground">{suggestedPayee.company}</p>}
                </div>
                {isEditable && (
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    <button
                      onClick={() => onVendorChange(suggestedPayee.id)}
                      className="rounded bg-green-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-700"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setShowManualSelect(true)}
                      className="rounded border border-gray-200 px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted/50"
                    >
                      Different vendor
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-1">
            {invoice.vendorNameRaw && (
              <p className="text-xs text-amber-600 mb-1">
                Detected: "{invoice.vendorNameRaw}" — select matching vendor below
              </p>
            )}
            {isEditable && (
              <SearchableSelect
                options={payees.map((p: SPPayee) => ({ value: p.id, label: p.name, subtitle: p.company || undefined }))}
                value=""
                onChange={onVendorChange}
                placeholder="Select vendor..."
              />
            )}
          </div>
        )}
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Invoice #</label>
          <p className="text-sm font-medium">{invoice.invoiceNumber || '—'}</p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Invoice Date</label>
          <p className="text-sm">{invoice.invoiceDate || '—'}</p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Due Date</label>
          {isEditable ? (
            <DateInput
              value={dueDate}
              onChange={(e) => {
                const v = e.target.value;
                setDueDate(v);
                void (async () => {
                  try {
                    const { spApi } = await import('@/lib/api-client');
                    await spApi.put(`/vendor-invoices/${invoice.id}`, { dueDate: v || null });
                  } catch {
                    /* ignore */
                  }
                })();
              }}
              className="w-full text-sm rounded border border-border px-2 py-0.5"
            />
          ) : (
            <p className="text-sm">{invoice.dueDate || '—'}</p>
          )}
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Summary</label>
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
            {(() => {
              const all = invoice.lines;
              const matched = all.filter((l) => l.isMatched && !l.isExcluded).length;
              const unmatched = all.filter((l) => !l.isMatched && !l.isCredit && !l.isExcluded).length;
              const priceOk = all.filter((l) => l.priceApplied && !l.isCredit).length;
              const pricePending = all.filter(
                (l) => l.isMatched && !l.priceApplied && !l.isCredit && !l.isExcluded,
              ).length;
              const excluded = all.filter((l) => l.isExcluded).length;
              return (
                <>
                  <span className="text-[10px] text-green-600">{matched} matched</span>
                  {unmatched > 0 && <span className="text-[10px] text-red-600">{unmatched} unmatched</span>}
                  <span className="text-[10px] text-green-600">{priceOk} priced</span>
                  {pricePending > 0 && <span className="text-[10px] text-amber-600">{pricePending} pending</span>}
                  {excluded > 0 && <span className="text-[10px] text-gray-400">{excluded} excluded</span>}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {invoice.notes && (
        <div>
          <label className="text-xs text-muted-foreground">Notes</label>
          <p className="text-sm">{invoice.notes}</p>
        </div>
      )}
    </div>
  );
}

// ── PO Link Section ──

function POLinkSection({ invoiceId }: { invoiceId: string }) {
  const { data: suggestData, isLoading } = useSuggestPO(invoiceId);
  const linkPO = useLinkPO();
  const [showManual, setShowManual] = useState(false);

  const suggested = suggestData?.data?.suggested ?? null;
  const available = suggestData?.data?.available ?? [];

  const handleLink = async (poId: string) => {
    try {
      await linkPO.mutateAsync({ id: invoiceId, purchaseOrderId: poId });
      toast.success('Invoice linked to Purchase Order');
    } catch (err: unknown) {
      toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to link PO');
    }
  };

  if (isLoading) return null;
  if (!suggested && available.length === 0) return null;

  return (
    <div className="space-y-2">
      {suggested && (
        <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50/50 p-3">
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-700">Match found: {suggested.orderNumber}</p>
              <p className="text-xs text-blue-600">
                {suggested.matchedItems} of {suggested.totalItems} items match
              </p>
            </div>
          </div>
          <button
            onClick={() => handleLink(suggested.id)}
            disabled={linkPO.isPending}
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {linkPO.isPending ? 'Linking...' : 'Link'}
          </button>
        </div>
      )}

      {available.length > 0 && (
        <div>
          {!showManual ? (
            <button
              onClick={() => setShowManual(true)}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <Link2 className="h-3 w-3" />
              {suggested ? 'Link to a different PO' : 'Link to Purchase Order'}
            </button>
          ) : (
            <div className="rounded-lg border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground">Select Purchase Order</p>
                <button
                  onClick={() => setShowManual(false)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {available.map((po) => (
                  <button
                    key={po.id}
                    onClick={() => handleLink(po.id)}
                    disabled={linkPO.isPending}
                    className="w-full flex items-center justify-between rounded px-2 py-1.5 text-left hover:bg-muted/50 disabled:opacity-50"
                  >
                    <span className="text-xs font-medium">{po.orderNumber}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {po.matchedItems}/{po.totalItems} items match
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Line Item Card ──

function LineItemCard({
  line,
  invoiceId,
  isEditable,
  payeeId,
}: {
  line: VendorInvoiceLine;
  invoiceId: string;
  isEditable: boolean;
  payeeId?: string | null;
}) {
  const updateLine = useUpdateInvoiceLine();
  const deleteLine = useDeleteInvoiceLine();
  const restoreLine = useRestoreInvoiceLine();
  const applyPrice = useApplyLinePrice();
  const confirmUnit = useConfirmUnit();
  const permissions = usePermissions();
  const canEditProducts = permissions.has('products.edit');
  // Applying a price writes the invoice line and the product's price.
  const canChangePrices = permissions.has('products.prices') && permissions.has('sp.invoices.edit');
  const canCreateProduct = permissions.has('products.add');
  const { selectedLocationId } = useLocationStore();
  const [editing, setEditing] = useState(false);
  const [editQty, setEditQty] = useState(line.quantity);
  // When unit detection is present, show detected unit (e.g. "case") instead of raw invoice unit ("each")
  const [editUnit, setEditUnit] = useState(
    line.detectedUnit && Number(line.detectedBaseQty) > 1 ? line.detectedUnit : line.unitOfMeasure || 'each',
  );
  const [editPrice, setEditPrice] = useState(line.unitPrice);
  const createProduct = useCreateProduct();
  const [showPriceApply, setShowPriceApply] = useState(false);
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  // Always fetch for matched items so we can detect discrepancies
  const { data: priceHistoryData } = useProductPriceHistory(line.isMatched ? line.productId : null);
  const [newProductName, setNewProductName] = useState(line.description);
  const [newProductBarcode, setNewProductBarcode] = useState(line.vendorItemCode || '');
  // Use per-each cost when unit detection is present (line.unitPrice may be case-level)
  const perEachCostForCreate =
    line.perEachCost != null && Number(line.detectedBaseQty) > 1 ? String(line.perEachCost) : line.unitPrice;
  const [newProductCost, setNewProductCost] = useState(perEachCostForCreate);
  const [newProductMargin, setNewProductMargin] = useState('30');
  const [newProductSellingPrice, setNewProductSellingPrice] = useState(
    String(Math.round((Number(perEachCostForCreate) / 0.7) * 100) / 100),
  );
  const defaultMargin = line.recommendedMarginPct ?? line.existingMarginPct ?? 30;
  // Use per-each cost for margin/selling price calculations (not case cost)
  const [editBaseQty, setEditBaseQty] = useState(String(Number(line.detectedBaseQty) || ''));
  const hasUnitDetection = Number(editBaseQty) > 1 && !!line.detectedUnit;
  const computedPerEachCost = hasUnitDetection
    ? Math.round((Number(line.unitPrice) / Number(editBaseQty)) * 100) / 100
    : Number(line.unitPrice);
  const perEachCostForPricing = computedPerEachCost;
  const [editMargin, setEditMargin] = useState(String(defaultMargin));
  const [editSellingPrice, setEditSellingPrice] = useState(
    String(
      line.recommendedSellingPrice ??
        (defaultMargin < 100 ? Math.round((perEachCostForPricing / (1 - defaultMargin / 100)) * 100) / 100 : ''),
    ),
  );
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const productScan = useBarcodeScan((code) => setProductSearch(code));

  const { data: productsData } = useProducts(
    {
      search: productSearch || line.description,
      limit: 20,
      ...(selectedLocationId ? { locationId: selectedLocationId } : {}),
    },
    { enabled: showProductSearch },
  );
  const products = productsData?.data ?? [];

  const confidence = line.matchConfidence ? Number(line.matchConfidence) : 0;
  const isPartialMatch = !line.isMatched && !!line.productId && confidence > 0;
  const isNoMatch = !line.isMatched && !line.productId && !line.isCredit;

  const handleSave = async () => {
    const qty = Number(editQty);
    const price = Number(editPrice);
    try {
      await updateLine.mutateAsync({
        invoiceId,
        lineId: line.id,
        quantity: qty,
        unitOfMeasure: editUnit,
        unitPrice: price,
        amount: qty * price,
      });
      setEditing(false);
      toast.success('Line updated');
    } catch (err: unknown) {
      toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to update line');
    }
  };

  const handleExclude = async () => {
    try {
      await deleteLine.mutateAsync({ invoiceId, lineId: line.id });
      toast.success('Line item excluded');
    } catch (err: unknown) {
      toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to exclude line');
    }
  };

  const handleRestore = async () => {
    try {
      await restoreLine.mutateAsync({ invoiceId, lineId: line.id });
      toast.success('Line item restored');
    } catch (err: unknown) {
      toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to restore line');
    }
  };

  const handleMapProduct = async (productId: string, productName: string) => {
    try {
      await updateLine.mutateAsync({
        invoiceId,
        lineId: line.id,
        productId,
        isMatched: true,
        matchConfidence: 1.0,
        matchMethod: 'manual',
      });
      setShowProductSearch(false);
      setProductSearch('');
      toast.success(`Mapped to "${productName}"`);
    } catch (err: unknown) {
      toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to map product');
    }
  };

  const handleConfirmMatch = async () => {
    try {
      await updateLine.mutateAsync({
        invoiceId,
        lineId: line.id,
        isMatched: true,
        matchMethod: 'confirmed',
        matchConfidence: 1.0,
      });
      toast.success('Match confirmed');
    } catch (err: unknown) {
      toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to confirm match');
    }
  };

  const handleRejectMatch = async () => {
    try {
      await updateLine.mutateAsync({
        invoiceId,
        lineId: line.id,
        productId: null,
        isMatched: false,
        matchConfidence: null,
        matchMethod: null,
      });
      toast.success('Suggestion dismissed');
    } catch (err: unknown) {
      toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to dismiss suggestion');
    }
  };

  const handleUnmap = async () => {
    try {
      await updateLine.mutateAsync({
        invoiceId,
        lineId: line.id,
        productId: null,
        isMatched: false,
        matchConfidence: null,
        matchMethod: null,
      });
      toast.success('Product unmatched');
    } catch (err: unknown) {
      toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to unmatch product');
    }
  };

  if (line.isExcluded) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3 text-sm opacity-50">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-400 shrink-0">{line.lineNumber}</span>
              <p className="font-medium text-gray-400 line-through truncate">{line.description}</p>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-400 mt-1 ml-6">
              <span>Qty: {line.quantity}</span>
              <span>× {formatCurrency(line.unitPrice)}</span>
              <span className="line-through">{formatCurrency(line.amount)}</span>
            </div>
          </div>
          {isEditable && (
            <button
              onClick={handleRestore}
              disabled={restoreLine.isPending}
              className="inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
              title="Restore this line item"
            >
              <RotateCcw className="h-3 w-3" />
              {restoreLine.isPending ? 'Restoring...' : 'Restore'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border text-sm ${
        line.isCredit
          ? 'border-green-200 bg-green-50/50'
          : line.isMatched
            ? line.priceApplied && line.priceChangeSeverity === 'same'
              ? 'border-border bg-muted/20 opacity-60'
              : line.priceApplied && line.priceChangeSeverity?.includes('decrease')
                ? 'border-green-200 border-l-4 border-l-green-500 bg-green-50/30'
                : line.priceChangeSeverity === 'significant_increase'
                  ? 'border-red-300 border-l-4 border-l-red-500'
                  : line.priceChangeSeverity === 'moderate_increase'
                    ? 'border-amber-200 border-l-4 border-l-amber-400'
                    : line.priceChangeSeverity?.includes('decrease')
                      ? 'border-green-200 border-l-4 border-l-green-500'
                      : 'border-border'
            : isPartialMatch
              ? 'border-amber-200 bg-amber-50/30'
              : 'border-red-200 bg-red-50/30'
      }`}
    >
      {/* Raw invoice line text */}
      {line.rawLineText && (
        <div className="px-3 pt-2 pb-1">
          <p className="text-[10px] text-muted-foreground italic leading-tight">Invoice line: {line.rawLineText}</p>
        </div>
      )}

      <div className="p-3 pt-1">
        {/* Line number + Description + Match Status Badge */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <span
              className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                line.isMatched
                  ? 'bg-green-100 text-green-700'
                  : isPartialMatch
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-red-100 text-red-700'
              }`}
            >
              {line.lineNumber}
            </span>
            <div className="min-w-0">
              <p className="font-medium text-foreground">{line.description}</p>
              {line.vendorItemCode && (
                <p className="text-[10px] text-muted-foreground mt-0.5">SKU: {line.vendorItemCode}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {line.isMatched ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                <CheckCircle2 className="h-3 w-3" /> Matched
              </span>
            ) : isPartialMatch ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                <AlertCircle className="h-3 w-3" /> Needs confirmation
              </span>
            ) : isNoMatch ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                <AlertCircle className="h-3 w-3" /> No match
              </span>
            ) : null}
            {line.isCredit && (
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                Credit
              </span>
            )}
          </div>
        </div>

        {/* Product mapping section — 3 tiers */}
        {!line.isCredit && isEditable && (
          <div className="mb-2">
            {/* Tier 1: Exact match — confirmed, with unmap option */}
            {line.isMatched ? (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="text-green-700 font-medium">{line.productName || 'Mapped to product'}</span>
                    <span
                      className="relative group cursor-help"
                      title={`Match: ${line.matchMethod || 'manual'}${line.productBarcode ? `\nBarcode: ${line.productBarcode}` : ''}`}
                    >
                      <Info className="h-3 w-3 text-muted-foreground" />
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {/* Unit detection badge */}
                    {line.detectedBaseQty != null &&
                      line.detectedBaseQty > 1 &&
                      (line.detectedUnitConfidence === 'high' ? (
                        <span
                          className="inline-flex items-center gap-0.5 text-[10px] text-green-600"
                          title={`Confirmed: ${line.detectedUnit || 'case'} of ${line.detectedBaseQty}`}
                        >
                          <Package className="h-3 w-3" /> {line.detectedUnit || 'case'} of {line.detectedBaseQty} ✓
                        </span>
                      ) : (
                        <button
                          onClick={async () => {
                            try {
                              await confirmUnit.mutateAsync({ invoiceId, lineId: line.id });
                              toast.success(
                                `Unit confirmed — ${line.detectedUnit || 'case'} of ${line.detectedBaseQty} saved`,
                              );
                            } catch (err: unknown) {
                              toast.error(
                                (err as { error?: { message?: string } })?.error?.message || 'Failed to confirm unit',
                              );
                            }
                          }}
                          disabled={confirmUnit.isPending || !canEditProducts}
                          className="inline-flex items-center gap-0.5 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 hover:bg-amber-100"
                          title={
                            canEditProducts
                              ? `Detected case of ${line.detectedBaseQty} from description — click to confirm and create unit conversion`
                              : 'Needs the Edit products permission'
                          }
                        >
                          <Package className="h-3 w-3" /> Case of {line.detectedBaseQty}?
                        </button>
                      ))}
                    {line.priceApplied ? (
                      line.priceSkipped ? (
                        <button
                          onClick={() => setShowPriceApply(!showPriceApply)}
                          className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:underline"
                          title="Price skipped — click to apply instead"
                        >
                          <SkipForward className="h-3 w-3" /> Skipped{showPriceApply ? '' : ' — edit'}
                        </button>
                      ) : line.priceChangePct != null && Math.abs(line.priceChangePct) < 1 ? (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] text-green-600 cursor-pointer hover:underline"
                          onClick={() => setShowPriceApply(!showPriceApply)}
                          title="No cost change — auto-approved. Click to edit."
                        >
                          <CheckCircle2 className="h-3 w-3" /> Auto-approved
                        </span>
                      ) : (
                        <button
                          onClick={() => setShowPriceApply(!showPriceApply)}
                          className="text-[10px] text-green-600 hover:underline"
                        >
                          ✓ Price applied{showPriceApply ? '' : ' — edit'}
                        </button>
                      )
                    ) : (
                      <>
                        <button
                          onClick={() => setShowPriceApply(!showPriceApply)}
                          className="inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-100"
                        >
                          <DollarSign className="h-3 w-3" /> Apply
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await updateLine.mutateAsync({
                                invoiceId,
                                lineId: line.id,
                                priceApplied: true,
                                priceSkipped: true,
                              });
                              toast.success('Price skipped — no change applied');
                            } catch (err: unknown) {
                              toast.error(
                                (err as { error?: { message?: string } })?.error?.message || 'Failed to skip price',
                              );
                            }
                          }}
                          disabled={updateLine.isPending}
                          className="inline-flex items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-muted/50"
                          title="Skip price change — keep current pricing"
                        >
                          <SkipForward className="h-3 w-3" /> Skip
                        </button>
                      </>
                    )}
                    <button onClick={handleUnmap} className="text-[10px] text-red-500 hover:underline">
                      Unmatch
                    </button>
                  </div>
                </div>

                {/* Expandable price editor */}
                {showPriceApply && (
                  <div className="mt-1 rounded border border-blue-200 bg-blue-50/50 p-2 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Editable units per case */}
                      <div>
                        <label className="text-[10px] text-muted-foreground">Units/Case</label>
                        <input
                          type="number"
                          min="1"
                          value={editBaseQty}
                          onChange={(e) => {
                            setEditBaseQty(e.target.value);
                            const bq = Number(e.target.value) || 1;
                            const invoicePrice = Number(line.unitPrice);
                            const newPerEach = bq > 1 ? Math.round((invoicePrice / bq) * 100) / 100 : invoicePrice;
                            const m = Number(editMargin) || 0;
                            if (m < 100 && newPerEach > 0)
                              setEditSellingPrice(String(Math.round((newPerEach / (1 - m / 100)) * 100) / 100));
                          }}
                          className="w-16 rounded border border-border px-1.5 py-0.5 text-xs text-center"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Cost (each)</label>
                        <p className="text-xs font-medium">{formatCurrency(perEachCostForPricing)}</p>
                        {hasUnitDetection && (
                          <p className="text-[9px] text-muted-foreground">
                            {line.detectedUnit}: {formatCurrency(line.unitPrice)}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Margin %</label>
                        <input
                          type="number"
                          step="0.1"
                          value={editMargin}
                          onChange={(e) => {
                            setEditMargin(e.target.value);
                            const m = Number(e.target.value) || 0;
                            const c = perEachCostForPricing;
                            if (m < 100 && c > 0)
                              setEditSellingPrice(String(Math.round((c / (1 - m / 100)) * 100) / 100));
                          }}
                          className="w-16 rounded border border-border px-1.5 py-0.5 text-xs text-center"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Sell (each)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editSellingPrice}
                          onChange={(e) => {
                            setEditSellingPrice(e.target.value);
                            const sp = Number(e.target.value) || 0;
                            const c = perEachCostForPricing;
                            if (sp > 0 && c > 0) setEditMargin(String(Math.round(((sp - c) / sp) * 1000) / 10));
                          }}
                          className="w-20 rounded border border-border px-1.5 py-0.5 text-xs text-center"
                        />
                        {hasUnitDetection && (
                          <p className="text-[9px] text-muted-foreground">
                            {line.detectedUnit}: {formatCurrency(Number(editSellingPrice) * Number(editBaseQty))}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={async () => {
                          try {
                            const margin = Number(editMargin);
                            const newBaseQty = Number(editBaseQty) || null;
                            const origBaseQty = Number(line.detectedBaseQty) || null;
                            // If user changed units/case, persist it on the line first
                            if (newBaseQty !== origBaseQty) {
                              await updateLine.mutateAsync({
                                invoiceId,
                                lineId: line.id,
                                detectedBaseQty: newBaseQty && newBaseQty > 1 ? newBaseQty : null,
                                detectedUnit:
                                  newBaseQty && newBaseQty > 1
                                    ? line.detectedUnit || line.unitOfMeasure || 'case'
                                    : null,
                              });
                            }
                            await applyPrice.mutateAsync({ invoiceId, lineId: line.id, marginPct: margin });
                            setShowPriceApply(false);
                            toast.success(
                              `Price applied: cost ${formatCurrency(perEachCostForPricing)}/each, selling ${formatCurrency(editSellingPrice)}/each`,
                            );
                          } catch (err: unknown) {
                            toast.error(
                              (err as { error?: { message?: string } })?.error?.message || 'Failed to apply price',
                            );
                          }
                        }}
                        disabled={applyPrice.isPending || updateLine.isPending || !canChangePrices}
                        title={canChangePrices ? undefined : 'Needs the Change prices permission'}
                        className="rounded bg-blue-600 px-2.5 py-1 text-[10px] font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {applyPrice.isPending || updateLine.isPending ? 'Applying...' : 'Confirm & Apply'}
                      </button>
                      <button
                        onClick={() => setShowPriceApply(false)}
                        className="text-[10px] text-muted-foreground hover:text-foreground"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : isPartialMatch ? (
              /* Tier 2: Partial match — suggested product, needs confirmation */
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-2 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-xs text-amber-700">
                      Suggested: <span className="font-medium">{line.productName || 'Unknown product'}</span>
                    </p>
                    <p className="text-[10px] text-amber-600">Matched via {line.matchMethod || 'name similarity'}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={handleConfirmMatch}
                      disabled={updateLine.isPending}
                      className="rounded bg-green-600 px-2 py-1 text-[10px] font-medium text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={handleRejectMatch}
                      disabled={updateLine.isPending}
                      className="rounded border border-red-200 px-2 py-1 text-[10px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => setShowProductSearch(true)}
                  className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline"
                >
                  <Plus className="h-2.5 w-2.5" /> Match a different product
                </button>
              </div>
            ) : showProductSearch ? (
              /* Manual product search */
              <div className="rounded-lg border border-border bg-white p-2 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    onKeyDown={productScan.onKeyDown}
                    onBlur={productScan.onBlur}
                    placeholder="Search products..."
                    autoFocus
                    className="flex-1 rounded border border-border px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                  />
                  <button
                    onClick={() => {
                      setShowProductSearch(false);
                      setProductSearch('');
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </button>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {products.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2 text-center">No products found</p>
                  ) : (
                    products.map((p: { id: string; name: string; primaryBarcode?: string; inStockQty?: number }) => (
                      <button
                        key={p.id}
                        onClick={() => handleMapProduct(p.id, p.name)}
                        disabled={updateLine.isPending}
                        className="w-full flex items-center justify-between rounded px-2 py-1.5 text-left hover:bg-muted/50 disabled:opacity-50"
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">{p.name}</p>
                          {p.primaryBarcode && <p className="text-[10px] text-muted-foreground">{p.primaryBarcode}</p>}
                        </div>
                        {p.inStockQty != null && (
                          <span className="text-[10px] text-muted-foreground shrink-0 ml-2">Stock: {p.inStockQty}</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : showCreateProduct && canCreateProduct ? (
              /* Create new product inline */
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-2 space-y-2">
                <p className="text-xs font-medium text-primary">Create New Product</p>
                <div>
                  <label className="text-[10px] text-muted-foreground">Product Name *</label>
                  <input
                    value={newProductName}
                    onChange={(e) => setNewProductName(e.target.value)}
                    placeholder="Product name"
                    className="w-full rounded border border-border px-2 py-1 text-xs"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground">Barcode / UPC</label>
                  <input
                    value={newProductBarcode}
                    onChange={(e) => setNewProductBarcode(e.target.value)}
                    placeholder="Enter barcode"
                    className="w-full rounded border border-border px-2 py-1 text-xs"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] text-muted-foreground">Cost *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newProductCost}
                      onChange={(e) => {
                        setNewProductCost(e.target.value);
                        const c = Number(e.target.value) || 0;
                        const m = Number(newProductMargin) || 30;
                        if (m < 100 && c > 0)
                          setNewProductSellingPrice(String(Math.round((c / (1 - m / 100)) * 100) / 100));
                      }}
                      className="w-full rounded border border-border px-2 py-1 text-xs"
                    />
                  </div>
                  <div className="w-16">
                    <label className="text-[10px] text-muted-foreground">Margin %</label>
                    <input
                      type="number"
                      step="0.1"
                      value={newProductMargin}
                      onChange={(e) => {
                        setNewProductMargin(e.target.value);
                        const m = Number(e.target.value) || 30;
                        const c = Number(newProductCost) || 0;
                        if (m < 100 && c > 0)
                          setNewProductSellingPrice(String(Math.round((c / (1 - m / 100)) * 100) / 100));
                      }}
                      className="w-full rounded border border-border px-2 py-1 text-xs"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-muted-foreground">Selling Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newProductSellingPrice}
                      onChange={(e) => {
                        setNewProductSellingPrice(e.target.value);
                        const sp = Number(e.target.value) || 0;
                        const c = Number(newProductCost) || 0;
                        if (sp > 0 && c > 0) setNewProductMargin(String(Math.round(((sp - c) / sp) * 1000) / 10));
                      }}
                      className="w-full rounded border border-border px-2 py-1 text-xs"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowCreateProduct(false)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={!newProductName.trim() || createProduct.isPending}
                    onClick={async () => {
                      try {
                        const cost = Number(newProductCost) || 0;
                        const sellingPrice = Number(newProductSellingPrice) || Math.round((cost / 0.7) * 100) / 100;

                        // Build locations, prices, units, payees arrays from invoice line data
                        const createPayload: Record<string, unknown> = {
                          name: newProductName.trim(),
                          primaryBarcode: newProductBarcode.trim() || undefined,
                          cost,
                          sellingPrice,
                          isActive: true,
                          locationId: selectedLocationId || undefined,
                          defaultSellingUnit: 'each',
                        };

                        // Assign to invoice location so product_locations + product_prices are created
                        if (selectedLocationId) {
                          createPayload.locations = [{ locationId: selectedLocationId }];
                          createPayload.prices = [{ locationId: selectedLocationId, cost, sellingPrice }];
                        }

                        // Create product_units row for case/pack conversion from unit detection
                        const detectedBaseQty = Number(line.detectedBaseQty) || 0;
                        if (detectedBaseQty > 1 && line.detectedUnit) {
                          const caseCost = Math.round(cost * detectedBaseQty * 100) / 100;
                          const caseSellingPrice = Math.round(sellingPrice * detectedBaseQty * 100) / 100;
                          createPayload.units = [
                            {
                              unitName: line.detectedUnit,
                              baseQty: detectedBaseQty,
                              cost: caseCost,
                              sellingPrice: caseSellingPrice,
                            },
                          ];
                        }

                        // Create product_payees mapping for the invoice vendor.
                        // Vendor mappings are location-agnostic (locationId: null)
                        // so the same vendor item code matches across all locations.
                        if (payeeId) {
                          createPayload.payees = [
                            {
                              payeeId,
                              locationId: null,
                              payeeItemCode: line.vendorItemCode || undefined,
                              vendorProductName: line.description,
                              vendorPrice: Number(line.unitPrice),
                              unitName: line.unitOfMeasure || 'each',
                            },
                          ];
                        }

                        const result = await createProduct.mutateAsync(createPayload);
                        const newId = (result as { data?: { id?: string } })?.data?.id;
                        if (newId) {
                          await handleMapProduct(newId, newProductName.trim());
                          setShowCreateProduct(false);
                          toast.success(`Product "${newProductName}" created and matched`);
                        }
                      } catch (err: unknown) {
                        toast.error(
                          (err as { error?: { message?: string } })?.error?.message || 'Failed to create product',
                        );
                      }
                    }}
                    className="rounded bg-primary px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
                  >
                    {createProduct.isPending ? 'Creating...' : 'Create & Match'}
                  </button>
                </div>
              </div>
            ) : (
              /* Tier 3: No match — manual mapping required */
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowProductSearch(true)}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <Plus className="h-3 w-3" /> Match product
                </button>
                {canCreateProduct && (
                  <button
                    onClick={() => setShowCreateProduct(true)}
                    className="inline-flex items-center gap-1 text-xs text-green-600 hover:underline"
                  >
                    <Plus className="h-3 w-3" /> Create new product
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Non-editable display for matched/partial items */}
        {!line.isCredit && !isEditable && line.productName && (
          <div className="mb-2 space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                Product: {line.productName}
                {line.productBarcode && <span className="ml-2 text-muted-foreground">({line.productBarcode})</span>}
              </span>
              {line.isMatched &&
                (line.priceApplied ? (
                  line.priceSkipped ? (
                    <button
                      onClick={() => setShowPriceApply(!showPriceApply)}
                      className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:underline"
                    >
                      <SkipForward className="h-3 w-3" /> Skipped
                    </button>
                  ) : line.priceChangePct != null && Math.abs(line.priceChangePct) < 1 ? (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] text-green-600 cursor-pointer hover:underline"
                      onClick={() => setShowPriceApply(!showPriceApply)}
                      title="No cost change — auto-approved. Click to edit."
                    >
                      <CheckCircle2 className="h-3 w-3" /> Auto-approved
                    </span>
                  ) : (
                    <button
                      onClick={() => setShowPriceApply(!showPriceApply)}
                      className="text-[10px] text-green-600 hover:underline"
                    >
                      ✓ Price applied{showPriceApply ? '' : ' — edit'}
                    </button>
                  )
                ) : (
                  <button
                    onClick={() => setShowPriceApply(!showPriceApply)}
                    className="inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-100"
                  >
                    <DollarSign className="h-3 w-3" /> Apply Price
                  </button>
                ))}
            </div>
            {showPriceApply && line.isMatched && (
              <div className="rounded border border-blue-200 bg-blue-50/50 p-2 space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <div>
                    <label className="text-[10px] text-muted-foreground">Units/Case</label>
                    <input
                      type="number"
                      min="1"
                      value={editBaseQty}
                      onChange={(e) => {
                        setEditBaseQty(e.target.value);
                        const bq = Number(e.target.value) || 1;
                        const invoicePrice = Number(line.unitPrice);
                        const newPerEach = bq > 1 ? Math.round((invoicePrice / bq) * 100) / 100 : invoicePrice;
                        const m = Number(editMargin) || 0;
                        if (m < 100 && newPerEach > 0)
                          setEditSellingPrice(String(Math.round((newPerEach / (1 - m / 100)) * 100) / 100));
                      }}
                      className="w-16 rounded border border-border px-1.5 py-0.5 text-xs text-center"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground">Cost (each)</label>
                    <p className="text-xs font-medium">{formatCurrency(perEachCostForPricing)}</p>
                    {hasUnitDetection && (
                      <p className="text-[9px] text-muted-foreground">
                        {line.detectedUnit}: {formatCurrency(line.unitPrice)}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground">Margin %</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editMargin}
                      onChange={(e) => {
                        setEditMargin(e.target.value);
                        const m = Number(e.target.value) || 0;
                        const c = perEachCostForPricing;
                        if (m < 100 && c > 0) setEditSellingPrice(String(Math.round((c / (1 - m / 100)) * 100) / 100));
                      }}
                      className="w-16 rounded border border-border px-1.5 py-0.5 text-xs text-center"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground">Sell (each)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editSellingPrice}
                      onChange={(e) => {
                        setEditSellingPrice(e.target.value);
                        const sp = Number(e.target.value) || 0;
                        const c = perEachCostForPricing;
                        if (sp > 0 && c > 0) setEditMargin(String(Math.round(((sp - c) / sp) * 1000) / 10));
                      }}
                      className="w-20 rounded border border-border px-1.5 py-0.5 text-xs text-center"
                    />
                    {hasUnitDetection && (
                      <p className="text-[9px] text-muted-foreground">
                        {line.detectedUnit}: {formatCurrency(Number(editSellingPrice) * Number(editBaseQty))}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      try {
                        const margin = Number(editMargin);
                        const newBaseQty = Number(editBaseQty) || null;
                        const origBaseQty = Number(line.detectedBaseQty) || null;
                        if (newBaseQty !== origBaseQty) {
                          await updateLine.mutateAsync({
                            invoiceId,
                            lineId: line.id,
                            detectedBaseQty: newBaseQty && newBaseQty > 1 ? newBaseQty : null,
                            detectedUnit:
                              newBaseQty && newBaseQty > 1 ? line.detectedUnit || line.unitOfMeasure || 'case' : null,
                          });
                        }
                        await applyPrice.mutateAsync({ invoiceId, lineId: line.id, marginPct: margin });
                        setShowPriceApply(false);
                        toast.success(
                          `Price applied: cost ${formatCurrency(perEachCostForPricing)}/each, selling ${formatCurrency(editSellingPrice)}/each`,
                        );
                      } catch (err: unknown) {
                        toast.error(
                          (err as { error?: { message?: string } })?.error?.message || 'Failed to apply price',
                        );
                      }
                    }}
                    disabled={applyPrice.isPending || !canChangePrices}
                    title={canChangePrices ? undefined : 'Needs the Change prices and vendor invoice edit permissions'}
                    className="rounded bg-blue-600 px-2.5 py-1 text-[10px] font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {applyPrice.isPending ? 'Applying...' : 'Confirm & Apply'}
                  </button>
                  <button
                    onClick={() => setShowPriceApply(false)}
                    className="text-[10px] text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quantity / Price / Total row */}
        {editing ? (
          <div className="flex items-center gap-2 mt-2">
            <div className="w-16">
              <label className="text-xs text-muted-foreground">Qty</label>
              <input
                type="number"
                value={editQty}
                onChange={(e) => setEditQty(e.target.value)}
                className="w-full rounded border border-border px-2 py-1 text-sm"
              />
            </div>
            <div className="w-20">
              <label className="text-xs text-muted-foreground">Unit</label>
              <select
                value={editUnit}
                onChange={(e) => setEditUnit(e.target.value)}
                className="w-full rounded border border-border px-2 py-1 text-sm bg-white"
              >
                <option value="each">each</option>
                <option value="case">case</option>
                <option value="box">box</option>
                <option value="lb">lb</option>
                <option value="kg">kg</option>
                <option value="oz">oz</option>
                <option value="pack">pack</option>
                <option value="dozen">dozen</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">Unit Price</label>
              <input
                type="number"
                step="0.01"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                className="w-full rounded border border-border px-2 py-1 text-sm"
              />
            </div>
            <div className="flex items-end gap-1 pb-0.5">
              <button
                onClick={handleSave}
                disabled={updateLine.isPending}
                className="rounded bg-primary px-2 py-1 text-xs text-white"
              >
                Save
              </button>
              <button
                onClick={() => setEditing(false)}
                className="rounded px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {/* Pricing breakdown grid */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-4">
                <span className="font-medium text-foreground">
                  {line.quantity} {line.detectedUnit || line.unitOfMeasure || 'each'}
                </span>
                {line.detectedBaseQty && line.detectedBaseQty > 1 ? (
                  <span className="text-[10px] text-muted-foreground">
                    ({line.detectedBaseQty} units/{line.detectedUnit || 'case'})
                  </span>
                ) : line.isMatched && line.unitBaseQty && line.unitBaseQty > 1 ? (
                  <span className="text-[10px] text-muted-foreground">
                    ({line.unitBaseQty} units/{line.productUnitName || 'case'})
                  </span>
                ) : null}
                <span>× {formatCurrency(line.unitPrice)}</span>
                {line.perEachCost != null && line.detectedBaseQty && line.detectedBaseQty > 1 && (
                  <span className="text-[10px] text-blue-600">({formatCurrency(line.perEachCost)}/each)</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">{formatCurrency(line.amount)}</span>
                {isEditable && (
                  <>
                    <button onClick={() => setEditing(true)} className="p-0.5 hover:bg-muted rounded">
                      <Pencil className="h-3 w-3 text-muted-foreground" />
                    </button>
                    <button
                      onClick={handleExclude}
                      className="p-0.5 hover:bg-red-50 rounded"
                      title="Exclude this line item"
                    >
                      <Trash2 className="h-3 w-3 text-red-400" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Pricing analysis for matched items */}
            {line.isMatched && !line.isCredit && (line.unitBaseQty || line.productCost != null) && (
              <div className="mt-1 space-y-0.5">
                {/* Row 1: Current location pricing */}
                <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
                  {line.productCost != null && <span>Current Cost: {formatCurrency(line.productCost)}</span>}
                  {line.productSellingPrice != null && line.productSellingPrice > 0 && (
                    <span>Current Selling: {formatCurrency(line.productSellingPrice)}</span>
                  )}
                  {line.existingMarginPct != null && <span>Current Margin: {line.existingMarginPct.toFixed(1)}%</span>}
                  {line.unitBaseQty && line.unitBaseQty > 1 && (
                    <span className="text-muted-foreground/60">
                      ({line.unitBaseQty} units/{line.productUnitName || 'case'})
                    </span>
                  )}
                </div>

                {/* Row 2: Price change with color coding */}
                {line.priceChangePct != null && (
                  <div
                    className={`inline-flex items-center gap-2 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                      line.priceChangeSeverity === 'significant_increase'
                        ? 'bg-red-100 text-red-700'
                        : line.priceChangeSeverity === 'moderate_increase'
                          ? 'bg-amber-100 text-amber-700'
                          : line.priceChangeSeverity === 'same'
                            ? 'bg-green-50 text-green-600'
                            : line.priceChangeSeverity === 'moderate_decrease'
                              ? 'bg-green-100 text-green-700'
                              : line.priceChangeSeverity === 'significant_decrease'
                                ? 'bg-green-200 text-green-800'
                                : 'text-muted-foreground'
                    }`}
                  >
                    {line.priceChangePct > 1 ? '▲' : line.priceChangePct < -1 ? '▼' : '='}{' '}
                    {Math.abs(line.priceChangePct).toFixed(1)}%{' '}
                    {line.priceChangePct > 1
                      ? 'cost increase'
                      : line.priceChangePct < -1
                        ? 'cost decrease'
                        : 'no change'}
                    {line.productCost != null && (
                      <span className="text-muted-foreground font-normal ml-1">
                        (was {formatCurrency(line.productCost)}, now{' '}
                        {formatCurrency(line.perEachCost ?? line.unitPrice)}/each)
                      </span>
                    )}
                  </div>
                )}

                {/* Row 3: Recommended new selling price */}
                {line.recommendedSellingPrice != null && (
                  <div className="text-[10px] text-blue-600">
                    Suggested selling price: {formatCurrency(line.recommendedSellingPrice)}
                    <span className="text-muted-foreground ml-1">
                      (
                      {line.recommendedMarginPct != null
                        ? `${line.recommendedMarginPct.toFixed(0)}% margin`
                        : '30% default margin'}
                      )
                    </span>
                    {line.productSellingPrice != null &&
                      line.productSellingPrice > 0 &&
                      line.recommendedSellingPrice !== line.productSellingPrice && (
                        <span
                          className={`ml-1 font-medium ${line.recommendedSellingPrice > line.productSellingPrice ? 'text-red-600' : 'text-green-600'}`}
                        >
                          {line.recommendedSellingPrice > line.productSellingPrice ? '▲' : '▼'}{' '}
                          {formatCurrency(Math.abs(line.recommendedSellingPrice - line.productSellingPrice))}
                        </span>
                      )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Cross-location price history */}
        {line.isMatched &&
          !line.isCredit &&
          (() => {
            // Compute discrepancy across locations
            const locations = priceHistoryData?.data?.locations ?? [];
            const costs = locations.filter((l) => l.currentCost != null).map((l) => l.currentCost!);
            let discrepancy: { hasIssue: boolean; message: string } = { hasIssue: false, message: '' };
            if (costs.length >= 2) {
              const minCost = Math.min(...costs);
              const maxCost = Math.max(...costs);
              if (minCost > 0) {
                const pctDiff = ((maxCost - minCost) / minCost) * 100;
                if (pctDiff > 1) {
                  const highLocs = locations.filter((l) => l.currentCost === maxCost).map((l) => l.locationName);
                  const lowLocs = locations.filter((l) => l.currentCost === minCost).map((l) => l.locationName);
                  discrepancy = {
                    hasIssue: true,
                    message: `${pctDiff.toFixed(1)}% cost gap: ${highLocs.join(', ')} at ${formatCurrency(maxCost)} vs ${lowLocs.join(', ')} at ${formatCurrency(minCost)}`,
                  };
                }
              }
            }
            // Also check invoice cost vs locations
            const invoiceCost = Number(line.unitPrice);
            const locsHigher = locations.filter((l) => l.currentCost != null && invoiceCost > l.currentCost * 1.01);
            const locsLower = locations.filter((l) => l.currentCost != null && invoiceCost < l.currentCost * 0.99);
            if (!discrepancy.hasIssue && locsHigher.length > 0 && locsLower.length > 0) {
              discrepancy = {
                hasIssue: true,
                message: `Invoice cost ${formatCurrency(invoiceCost)} is higher than ${locsLower.map((l) => l.locationName).join(', ')} but lower than ${locsHigher.map((l) => l.locationName).join(', ')}`,
              };
            } else if (!discrepancy.hasIssue && locsHigher.length > 0) {
              discrepancy = {
                hasIssue: true,
                message: `Invoice cost ${formatCurrency(invoiceCost)} is higher than all locations (${locsHigher.map((l) => `${l.locationName}: ${formatCurrency(l.currentCost!)}`).join(', ')})`,
              };
            }

            return (
              <div className="mt-1">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowPriceHistory(!showPriceHistory)}
                    className="text-[10px] text-primary hover:underline"
                  >
                    {showPriceHistory ? 'Hide' : 'View'} price across locations
                  </button>
                  {discrepancy.hasIssue && !showPriceHistory && (
                    <button
                      onClick={() => setShowPriceHistory(true)}
                      className="inline-flex items-center gap-1 text-[10px] text-amber-600"
                      title={discrepancy.message}
                    >
                      <AlertCircle className="h-3 w-3" />
                      <span className="hidden sm:inline">
                        {discrepancy.message.length > 60
                          ? discrepancy.message.slice(0, 60) + '...'
                          : discrepancy.message}
                      </span>
                    </button>
                  )}
                </div>
                {showPriceHistory && priceHistoryData?.data?.locations && (
                  <div className="mt-1 overflow-x-auto rounded border border-border">
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="bg-muted/50">
                          <th className="px-2 py-1 text-left font-medium text-muted-foreground">Location</th>
                          <th className="px-2 py-1 text-right font-medium text-muted-foreground">Current Selling</th>
                          <th className="px-2 py-1 text-right font-medium text-muted-foreground">Invoice Cost</th>
                          <th className="px-2 py-1 text-right font-medium text-muted-foreground">Current Cost</th>
                          <th className="px-2 py-1 text-left font-medium text-muted-foreground">Vendor</th>
                          <th className="px-2 py-1 text-left font-medium text-muted-foreground">Effective Date</th>
                          <th className="px-2 py-1 text-left font-medium text-muted-foreground">Invoice #</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {priceHistoryData.data.locations.map((loc) => (
                          <tr key={loc.locationId} className="hover:bg-muted/30">
                            <td className="px-2 py-1 font-medium">{loc.locationName}</td>
                            <td className="px-2 py-1 text-right">
                              {loc.currentSellingPrice != null ? formatCurrency(loc.currentSellingPrice) : '—'}
                            </td>
                            <td
                              className={`px-2 py-1 text-right font-medium ${
                                loc.currentCost != null && Number(line.unitPrice) > loc.currentCost
                                  ? 'text-red-600'
                                  : loc.currentCost != null && Number(line.unitPrice) < loc.currentCost
                                    ? 'text-green-600'
                                    : ''
                              }`}
                            >
                              {formatCurrency(line.unitPrice)}
                            </td>
                            <td className="px-2 py-1 text-right">
                              {loc.currentCost != null ? formatCurrency(loc.currentCost) : '—'}
                            </td>
                            <td className="px-2 py-1">{loc.history[0]?.vendorName || '—'}</td>
                            <td className="px-2 py-1">{loc.history[0]?.invoiceDate || '—'}</td>
                            <td className="px-2 py-1">{loc.history[0]?.invoiceNumber || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}

        {line.adjustedQuantity && (
          <p className="text-xs text-amber-600 mt-1">
            Adjusted qty: {line.adjustedQuantity} {line.adjustmentReason ? `— ${line.adjustmentReason}` : ''}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Add Line Button ──

function AddLineButton({ invoiceId }: { invoiceId: string }) {
  const addLine = useAddInvoiceLine();
  const { selectedLocationId } = useLocationStore();
  const [showForm, setShowForm] = useState(false);
  const [desc, setDesc] = useState('');
  const [qty, setQty] = useState('1');
  const [price, setPrice] = useState('0');
  const [uom, setUom] = useState('each');
  const [isCredit, setIsCredit] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const productScan = useBarcodeScan((code) => setProductSearch(code));
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [numUnits, setNumUnits] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [margin, setMargin] = useState('30');

  const { data: productsData } = useProducts(
    {
      search: productSearch,
      limit: 20,
      ...(selectedLocationId ? { locationId: selectedLocationId } : {}),
    },
    { enabled: showProductSearch && productSearch.length > 0 },
  );
  const products = productsData?.data ?? [];

  const resetForm = () => {
    setShowForm(false);
    setDesc('');
    setQty('1');
    setPrice('0');
    setUom('each');
    setIsCredit(false);
    setSelectedProduct(null);
    setProductSearch('');
    setShowProductSearch(false);
    setBarcode('');
    setNumUnits('');
    setSellingPrice('');
    setMargin('30');
  };

  const handleSelectProduct = (product: {
    id: string;
    name: string;
    sellingPrice?: number;
    primaryBarcode?: string;
  }) => {
    setSelectedProduct({ id: product.id, name: product.name });
    if (!desc.trim()) setDesc(product.name);
    if (product.sellingPrice && Number(sellingPrice) === 0) setSellingPrice(String(product.sellingPrice));
    if (product.primaryBarcode && !barcode.trim()) setBarcode(product.primaryBarcode);
    setShowProductSearch(false);
    setProductSearch('');
  };

  const handleAdd = async () => {
    if (!desc.trim()) return;
    const q = Number(qty) || 1;
    const p = Number(price) || 0;
    const units = Number(numUnits) || 0;
    try {
      await addLine.mutateAsync({
        invoiceId,
        description: desc,
        quantity: q,
        unitOfMeasure: uom,
        unitPrice: p,
        amount: q * p,
        isCredit,
        vendorItemCode: barcode.trim() || undefined,
        ...(units > 1
          ? {
              detectedUnit: uom !== 'each' ? uom : 'case',
              detectedBaseQty: units,
            }
          : {}),
        ...(Number(sellingPrice) > 0 ? { sellingPrice: Number(sellingPrice) } : {}),
        ...(selectedProduct
          ? {
              productId: selectedProduct.id,
              isMatched: true,
              matchMethod: 'manual',
              matchConfidence: 1.0,
            }
          : {}),
      });
      resetForm();
      toast.success('Line added');
    } catch (err: unknown) {
      toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to add line');
    }
  };

  if (!showForm) {
    return (
      <div className="flex gap-2">
        <button
          onClick={() => {
            setIsCredit(false);
            setShowForm(true);
          }}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <Plus className="h-3.5 w-3.5" /> Add Item
        </button>
        <button
          onClick={() => {
            setIsCredit(true);
            setShowForm(true);
          }}
          className="inline-flex items-center gap-1 text-xs text-green-600 hover:underline"
        >
          <Plus className="h-3.5 w-3.5" /> Add Credit
        </button>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border p-3 space-y-2 ${isCredit ? 'border-green-200 bg-green-50/30' : 'border-border'}`}
    >
      <p className="text-xs font-medium">{isCredit ? 'Add Credit Line' : 'Add Line Item'}</p>

      {/* Product selection */}
      <div>
        {selectedProduct ? (
          <div className="flex items-center justify-between rounded border border-green-200 bg-green-50/50 px-2 py-1.5">
            <div className="flex items-center gap-1.5 min-w-0">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
              <span className="text-xs font-medium text-foreground truncate">{selectedProduct.name}</span>
            </div>
            <button
              onClick={() => setSelectedProduct(null)}
              className="text-xs text-muted-foreground hover:text-foreground shrink-0 ml-2"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : showProductSearch ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                onKeyDown={productScan.onKeyDown}
                onBlur={productScan.onBlur}
                placeholder="Search products..."
                autoFocus
                className="flex-1 rounded border border-border px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
              />
              <button
                onClick={() => {
                  setShowProductSearch(false);
                  setProductSearch('');
                }}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </div>
            {productSearch.length > 0 && (
              <div className="max-h-40 overflow-y-auto rounded border border-border bg-white space-y-0.5 p-1">
                {products.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2 text-center">No products found</p>
                ) : (
                  products.map(
                    (p: {
                      id: string;
                      name: string;
                      primaryBarcode?: string;
                      inStockQty?: number;
                      sellingPrice?: number;
                      sku?: string;
                    }) => (
                      <button
                        key={p.id}
                        onClick={() => handleSelectProduct(p)}
                        className="w-full flex items-center justify-between rounded px-2 py-1.5 text-left hover:bg-muted/50"
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">{p.name}</p>
                          {p.primaryBarcode && <p className="text-[10px] text-muted-foreground">{p.primaryBarcode}</p>}
                        </div>
                        {p.sellingPrice != null && (
                          <span className="text-[10px] text-muted-foreground shrink-0 ml-2">
                            ${Number(p.sellingPrice).toFixed(2)}
                          </span>
                        )}
                      </button>
                    ),
                  )
                )}
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => setShowProductSearch(true)}
            className={`inline-flex items-center gap-1 text-xs ${isCredit ? 'text-green-600' : 'text-primary'} hover:underline`}
          >
            <Plus className="h-3 w-3" /> {isCredit ? 'Link to product (optional)' : 'Select product'}
          </button>
        )}
      </div>

      <input
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        placeholder="Description"
        className="w-full rounded border border-border px-2 py-1.5 text-sm"
      />
      <div className="flex gap-2">
        <div className="flex-1">
          <label className="text-[10px] text-muted-foreground">Barcode / SKU</label>
          <input
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            placeholder="Barcode or vendor SKU"
            className="w-full rounded border border-border px-2 py-1.5 text-sm"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <div className="w-16">
          <label className="text-[10px] text-muted-foreground">Qty</label>
          <input
            type="number"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder="Qty"
            className="w-full rounded border border-border px-2 py-1.5 text-sm"
          />
        </div>
        <div className="w-20">
          <label className="text-[10px] text-muted-foreground">Unit</label>
          <select
            value={uom}
            onChange={(e) => setUom(e.target.value)}
            className="w-full rounded border border-border px-2 py-1.5 text-sm bg-white"
          >
            <option value="each">each</option>
            <option value="case">case</option>
            <option value="box">box</option>
            <option value="lb">lb</option>
            <option value="kg">kg</option>
            <option value="oz">oz</option>
            <option value="pack">pack</option>
            <option value="dozen">dozen</option>
            <option value="pallet">pallet</option>
          </select>
        </div>
        <div className="w-20">
          <label className="text-[10px] text-muted-foreground">Units/Case</label>
          <input
            type="number"
            value={numUnits}
            onChange={(e) => {
              setNumUnits(e.target.value);
              const units = Number(e.target.value) || 0;
              const p = Number(price) || 0;
              if (units > 1 && p > 0) {
                const perEach = Math.round((p / units) * 100) / 100;
                const m = Number(margin) || 30;
                if (m < 100) setSellingPrice(String(Math.round((perEach / (1 - m / 100)) * 100) / 100));
              }
            }}
            placeholder="e.g. 12"
            className="w-full rounded border border-border px-2 py-1.5 text-sm"
          />
        </div>
        <div className="flex-1">
          <label className="text-[10px] text-muted-foreground">Cost Price</label>
          <input
            type="number"
            step="0.01"
            value={price}
            onChange={(e) => {
              setPrice(e.target.value);
              const p = Number(e.target.value) || 0;
              const units = Number(numUnits) || 0;
              const m = Number(margin) || 30;
              const costEach = units > 1 ? p / units : p;
              if (costEach > 0 && m < 100) setSellingPrice(String(Math.round((costEach / (1 - m / 100)) * 100) / 100));
            }}
            placeholder="Unit Price"
            className="w-full rounded border border-border px-2 py-1.5 text-sm"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <div className="w-16">
          <label className="text-[10px] text-muted-foreground">Margin %</label>
          <input
            type="number"
            step="0.1"
            value={margin}
            onChange={(e) => {
              setMargin(e.target.value);
              const m = Number(e.target.value) || 0;
              const p = Number(price) || 0;
              const units = Number(numUnits) || 0;
              const costEach = units > 1 ? p / units : p;
              if (m < 100 && costEach > 0) setSellingPrice(String(Math.round((costEach / (1 - m / 100)) * 100) / 100));
            }}
            className="w-full rounded border border-border px-2 py-1.5 text-sm"
          />
        </div>
        <div className="flex-1">
          <label className="text-[10px] text-muted-foreground">Selling Price (each)</label>
          <input
            type="number"
            step="0.01"
            value={sellingPrice}
            onChange={(e) => {
              setSellingPrice(e.target.value);
              const sp = Number(e.target.value) || 0;
              const p = Number(price) || 0;
              const units = Number(numUnits) || 0;
              const costEach = units > 1 ? p / units : p;
              if (sp > 0 && costEach > 0) setMargin(String(Math.round(((sp - costEach) / sp) * 1000) / 10));
            }}
            placeholder="Selling price"
            className="w-full rounded border border-border px-2 py-1.5 text-sm"
          />
        </div>
      </div>
      {Number(numUnits) > 1 && Number(price) > 0 && (
        <p className="text-[10px] text-muted-foreground">
          Per-each cost: {formatCurrency(Number(price) / Number(numUnits))}
        </p>
      )}
      {Number(qty) > 0 && Number(price) > 0 && (
        <p className="text-xs text-muted-foreground text-right">Total: {formatCurrency(Number(qty) * Number(price))}</p>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={resetForm} className="text-xs text-muted-foreground hover:text-foreground">
          Cancel
        </button>
        <button
          onClick={handleAdd}
          disabled={!desc.trim() || addLine.isPending}
          className="rounded bg-primary px-3 py-1 text-xs text-white disabled:opacity-50"
        >
          {addLine.isPending ? 'Adding...' : 'Add'}
        </button>
      </div>
    </div>
  );
}

// ── Invoice Attachments ──

function InvoiceAttachments({ invoiceId, isEditable }: { invoiceId: string; isEditable: boolean }) {
  const { data: attachmentsData } = useInvoiceAttachments(invoiceId);
  const uploadAttachment = useUploadAttachment();
  const deleteAttachment = useDeleteAttachment();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachments = attachmentsData?.data ?? [];

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const handleUpload = async (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`"${file.name}" is ${(file.size / (1024 * 1024)).toFixed(1)}MB — max allowed is 10MB`);
      return;
    }
    try {
      await uploadAttachment.mutateAsync({ invoiceId, file });
      toast.success(`"${file.name}" uploaded`);
    } catch (err: unknown) {
      toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to upload attachment');
    }
  };

  const handleDownload = async (attachmentId: string, filename: string) => {
    try {
      const res = await fetch(`/sp-api/v1/vendor-invoices/${invoiceId}/attachments/${attachmentId}/download`, {
        credentials: 'include',
      });
      const json = await res.json();
      if (json.data?.url) {
        const a = document.createElement('a');
        a.href = json.data.url;
        a.download = filename;
        a.target = '_blank';
        a.click();
      }
    } catch (err: unknown) {
      toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to download');
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-foreground">
          Attachments {attachments.length > 0 && `(${attachments.length})`}
        </label>
        {isEditable && (
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadAttachment.isPending}
              className="text-[10px] text-primary hover:underline"
            >
              {uploadAttachment.isPending ? 'Uploading...' : '+ Add file'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUpload(file);
                e.target.value = '';
              }}
            />
          </>
        )}
      </div>
      {attachments.length === 0 ? (
        <p className="text-[10px] text-muted-foreground">No attachments</p>
      ) : (
        <div className="space-y-1">
          {attachments.map((att) => (
            <div key={att.id} className="flex items-center justify-between rounded bg-muted/30 px-2 py-1">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] font-medium truncate">{att.filename}</p>
                  <p className="text-[10px] text-muted-foreground">{formatSize(att.file_size)}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => handleDownload(att.id, att.filename)}
                  className="p-0.5 hover:bg-muted rounded"
                  title="Download"
                >
                  <Download className="h-3 w-3 text-primary" />
                </button>
                {isEditable && (
                  <button
                    onClick={async () => {
                      try {
                        await deleteAttachment.mutateAsync({ invoiceId, attachmentId: att.id });
                        toast.success('Attachment removed');
                      } catch (err: unknown) {
                        toast.error(
                          (err as { error?: { message?: string } })?.error?.message || 'Failed to remove attachment',
                        );
                      }
                    }}
                    className="p-0.5 hover:bg-red-50 rounded"
                    title="Remove"
                  >
                    <Trash2 className="h-3 w-3 text-red-400" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Inventory Update Confirmation Dialog ──

function InventoryConfirmDialog({
  invoiceId,
  onConfirm,
  onCancel,
  isPending,
}: {
  invoiceId: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const { data: previewData, isLoading } = useApprovalPreview(invoiceId, true);
  const items = previewData?.data?.items ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg max-h-[70vh] rounded-xl bg-white shadow-xl flex flex-col">
        <div className="p-5 border-b border-border">
          <h3 className="text-sm font-semibold">Update Inventory</h3>
          <p className="text-xs text-muted-foreground mt-1">
            The following quantities will be added to your inventory.
          </p>
        </div>
        <div className="flex-1 overflow-auto p-5">
          {isLoading ? (
            <div className="flex h-20 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="pb-2 text-left font-medium text-muted-foreground">Product</th>
                  <th className="pb-2 text-right font-medium text-muted-foreground">Qty</th>
                  <th className="pb-2 text-right font-medium text-muted-foreground">Before</th>
                  <th className="pb-2 text-right font-medium text-muted-foreground">After</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((item) => (
                  <tr key={item.productId + item.lineNumber}>
                    <td className="py-2 font-medium truncate max-w-[220px]">{item.productName}</td>
                    <td className="py-2 text-right">
                      <span>
                        +{item.quantity} {item.unitOfMeasure}
                      </span>
                      {item.converted && (
                        <span className="block text-[9px] text-muted-foreground">
                          ({item.invoiceQuantity} {item.invoiceUnit} × converted)
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-right text-muted-foreground">{item.currentStock}</td>
                    <td className="py-2 text-right font-medium text-green-600">{item.afterStock}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="p-5 border-t border-border flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-xs text-muted-foreground hover:text-foreground">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending || isLoading}
            className="rounded-lg bg-primary px-5 py-2 text-xs font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {isPending ? 'Updating...' : 'Confirm & Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Approve Confirmation Dialog ──

function ApproveConfirmDialog({
  invoiceId,
  updateInventory,
  onConfirm,
  onCancel,
  isPending,
}: {
  invoiceId: string;
  updateInventory: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const { data: previewData, isLoading } = useApprovalPreview(invoiceId, true);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;
  const items = previewData?.data?.items ?? [];
  const totalPages = Math.ceil(items.length / PAGE_SIZE);
  const pageItems = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-2xl max-h-[80vh] rounded-xl bg-white shadow-xl flex flex-col">
        <div className="p-5 border-b border-border">
          <h3 className="text-sm font-semibold">Confirm Invoice Approval</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Review the changes below before approving.
            {updateInventory && ' Inventory quantities will be updated.'}
          </p>
        </div>

        <div className="flex-1 overflow-auto p-5">
          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={ClipboardCheck}
              title="No matched items to review"
              description="Matched invoice lines will appear here for approval."
              className="py-8"
            />
          ) : (
            <>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    <th className="pb-2 text-left font-medium text-muted-foreground">#</th>
                    <th className="pb-2 text-left font-medium text-muted-foreground">Product</th>
                    <th className="pb-2 text-right font-medium text-muted-foreground">Qty</th>
                    <th className="pb-2 text-right font-medium text-muted-foreground">Unit Price</th>
                    <th className="pb-2 text-right font-medium text-muted-foreground">Amount</th>
                    {updateInventory && (
                      <>
                        <th className="pb-2 text-right font-medium text-muted-foreground">Before</th>
                        <th className="pb-2 text-right font-medium text-muted-foreground">After</th>
                      </>
                    )}
                    <th className="pb-2 text-center font-medium text-muted-foreground">Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pageItems.map((item) => (
                    <tr key={item.productId + item.lineNumber} className="hover:bg-muted/30">
                      <td className="py-2">{item.lineNumber}</td>
                      <td className="py-2">
                        <p className="font-medium truncate max-w-[200px]">{item.productName}</p>
                      </td>
                      <td className="py-2 text-right">
                        {item.quantity} {item.unitOfMeasure}
                      </td>
                      <td className="py-2 text-right">{formatCurrency(item.unitPrice)}</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(item.amount)}</td>
                      {updateInventory && (
                        <>
                          <td className="py-2 text-right text-muted-foreground">{item.currentStock}</td>
                          <td className="py-2 text-right font-medium text-green-600">{item.afterStock}</td>
                        </>
                      )}
                      <td className="py-2 text-center">
                        {item.priceApplied ? (
                          <span className="text-green-600">✓</span>
                        ) : (
                          <span className="text-amber-600">⏭</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                  <span className="text-[10px] text-muted-foreground">
                    Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, items.length)} of {items.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(Math.max(0, page - 1))}
                      disabled={page === 0}
                      className="rounded border border-border px-2 py-1 text-xs disabled:opacity-30"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-muted-foreground">
                      {page + 1} / {totalPages}
                    </span>
                    <button
                      onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                      disabled={page >= totalPages - 1}
                      className="rounded border border-border px-2 py-1 text-xs disabled:opacity-30"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-5 border-t border-border flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-xs text-muted-foreground hover:text-foreground">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending || isLoading}
            className="rounded-lg bg-green-600 px-5 py-2 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {isPending ? 'Approving...' : 'Confirm & Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bills Tab ──

export function BillsTab() {
  const { data: billsData, isLoading } = useVendorInvoiceBills();
  const bills = billsData?.data ?? [];
  const [selectedBillId, setSelectedBillId] = useState<string | null>(null);
  const [showPayModal, setShowPayModal] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  // Paying a bill sends money, so it has its own key.
  const canPay = usePermissions().has('sp.bills.pay');

  const total = bills.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(() => bills.slice((page - 1) * perPage, page * perPage), [bills, page, perPage]);

  if (selectedBillId) {
    return <InvoiceReviewPanel invoiceId={selectedBillId} onBack={() => setSelectedBillId(null)} />;
  }

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (bills.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border">
        <EmptyState icon={Receipt} title="No bills yet" description="Approved invoices will appear here." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border border-border">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Bill #</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Vendor Invoice #</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Vendor</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Invoice Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Due Date</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-white">
            {pageRows.map((bill) => (
              <tr key={bill.id} className="hover:bg-muted/30">
                <td className="px-4 py-3 text-sm font-bold text-primary">{bill.billNumber || '—'}</td>
                <td className="px-4 py-3 text-sm font-medium">{bill.invoiceNumber || '—'}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{bill.payee?.name || '—'}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{bill.invoiceDate || '—'}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{bill.dueDate || '—'}</td>
                <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(bill.totalAmount)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={bill.status} />
                    {(bill as any).paymentSource === 'auto_pay' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                        Auto-pay
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => setSelectedBillId(bill.id)} className="text-xs text-primary hover:underline">
                      View
                    </button>
                    {bill.status === 'approved' && canPay && (
                      <button
                        onClick={() => setShowPayModal(bill.id)}
                        className="rounded bg-primary px-2.5 py-1 text-xs font-medium text-white hover:bg-primary/90"
                      >
                        Pay
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DataTablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
        perPageOptions={PER_PAGE_OPTIONS}
        onPerPageChange={(n) => {
          setPerPage(n);
          setPage(1);
        }}
      />

      {showPayModal && canPay && <PayBillModal billId={showPayModal} onClose={() => setShowPayModal(null)} />}
    </div>
  );
}

// ── Pay Bill Modal ──

function PayBillModal({ billId, onClose }: { billId: string; onClose: () => void }) {
  const { data: invoiceData } = useVendorInvoice(billId);
  const payBill = usePayBill();

  const invoice = invoiceData?.data;
  // Locked to the invoice location's primary funding account (from onboarding).
  const { account: lockedAccount, isLoading: lockedAccountLoading } = useLockedFundingAccount(
    invoice?.locationId ?? undefined,
  );
  const fundingAccountId = lockedAccount?.id ?? '';
  const [memo, setMemo] = useState('');

  if (!invoice) return null;

  const handlePay = async () => {
    if (!fundingAccountId) {
      toast.error('No funding account is set up for this location');
      return;
    }
    try {
      await payBill.mutateAsync({ id: billId, fundingAccountId, memo });
      toast.success('Payment created');
      onClose();
    } catch (err: unknown) {
      toast.error((err as { error?: { message?: string } })?.error?.message || 'Failed to create payment');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold">Pay Bill</h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted">
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="rounded-lg bg-muted/50 p-3 space-y-1">
            <p className="text-sm font-medium">Invoice #{invoice.invoiceNumber || invoice.id.slice(0, 8)}</p>
            <p className="text-sm text-muted-foreground">Vendor: {invoice.payee?.name || '—'}</p>
            <p className="text-lg font-semibold">{formatCurrency(invoice.totalAmount)}</p>
          </div>

          <div>
            <label className="text-sm font-medium">Funding Account</label>
            <div
              aria-readonly="true"
              className="mt-1 w-full cursor-not-allowed rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm"
            >
              {lockedAccount
                ? `${lockedAccount.nickname} (${lockedAccount.bankName})`
                : lockedAccountLoading
                  ? 'Loading...'
                  : 'No funding account set up for this location'}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Memo (optional)</label>
            <input
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder={`Payment for Invoice #${invoice.invoiceNumber || ''}`}
              className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm"
            />
          </div>

          <button
            onClick={handlePay}
            disabled={!fundingAccountId || payBill.isPending}
            className="w-full rounded-lg bg-primary py-2.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {payBill.isPending ? 'Processing...' : `Pay ${formatCurrency(invoice.totalAmount)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
